import { useCallback, useEffect, useMemo, useRef, useState, type DragEvent, type FormEvent, type ReactNode } from "react";
import { AgentQuestionsCard } from "./agent-questions-card";
import {
  buildAnswersDoc, CODING_ASSISTANT_USER_ID, extractAgentQuestions, isAgentComment, stripAgentMarker,
  withoutQuestionsBlock, type AgentAnswer,
} from "./agent-questions";
import { BoardSettings } from "./board-settings";
import type { TaskRouteMode } from "./portal-routes";
import { docToPlainText, wrapPlainTextAsDoc, type TiptapDoc } from "./rich-text-doc";
import { RichTextEditor, RichTextView } from "./rich-text-editor";
import { SprintCreateForm } from "./sprint-form";
import { IssueTypeIcon, ISSUE_TYPES, PriorityIcon, PRIORITY_LEVELS, issueTypeMeta, priorityMeta } from "./task-icons";
import {
  AvatarContent,
  ErrorBanner,
  RequiredMark,
  Skeleton,
  useClickOutsideToClose,
  useConfirm,
  useModalA11y,
  useToast,
} from "./ui-kit";
import { formatServerDateTime, parseServerTime, relativeTimeFrom } from "./server-time";

type Member = { avatarUrl?: string | null; displayName: string; id: string };
// Beyond this many, the assignee filter's avatar row overflows into a "+N" menu
// instead of just cutting the rest of the team off with no way to filter by them.
const ASSIGNEE_AVATAR_LIMIT = 5;
// Mirrors MAX_TITLE_LENGTH in tasks-api.ts -- the form surfaces the limit
// inline before submit; the API is still the authority and returns
// TITLE_TOO_LONG (400) for anything that slips past.
const MAX_TITLE_LENGTH = 500;
type Column = {
  category: "backlog" | "todo" | "in_progress" | "done";
  id: string;
  isReview: number;
  name: string;
  position: number;
  wipLimit: number | null;
};
type Task = {
  aliases: string[];
  assigneeId: string;
  assigneeName: string;
  columnId: string;
  commentCount: number;
  dueOn: string | null;
  description: TiptapDoc | null;
  epicKey: string | null;
  epicTitle: string | null;
  estimatePoints: number | null;
  externalLinkUrl: string | null;
  goalId: string | null;
  id: string;
  labels: string[];
  lastCommentAt: string | null;
  parentId: string | null;
  position: number;
  priority: string;
  pullRequests: Array<{
    approvalCount: number | null;
    errorMessage: string | null;
    lastActivityAt: string | null;
    mergeStatus: string | null;
    pullRequestId: string;
    repositoryName: string;
    status: string;
    title: string;
    url: string;
  }>;
  reporterId: string | null;
  reporterName: string | null;
  reviewers: Array<{
    approvedAt: string | null;
    avatarUrl: string | null;
    displayName: string;
    reviewerId: string;
    status: "pending" | "approved";
  }>;
  sprintId: string | null;
  startsOn: string | null;
  status: "open" | "in_progress" | "done" | "cancelled";
  targetOn: string | null;
  taskKey: string;
  title: string;
  type: "epic" | "story" | "task" | "subtask" | "bug";
};
type CommentAttachment = { contentType: string; fileName: string; id: string; sizeBytes: number };
type TaskComment = {
  attachments: CommentAttachment[];
  authorId: string;
  authorName: string;
  body: TiptapDoc;
  createdAt: string;
  id: string;
  updatedAt: string;
};
// A freshly-uploaded-but-not-yet-submitted attachment, staged locally until
// the comment it belongs to is actually posted/saved (see
// presignAndUploadFile below and parseAttachmentInputs in tasks-api.ts,
// which this shape matches exactly).
type PendingAttachment = { attachmentId: string; contentType: string; fileName: string; key: string; sizeBytes: number };
type DependencyTask = { id: string; status: Task["status"]; taskKey: string; title: string };
type HistoryChange = { after: string | null; before: string | null; field: string; label: string };
type HistoryEntry = { actorName: string; changes: HistoryChange[]; createdAt: string; eventType: string; id: string };
type TaskData = {
  columns: Column[];
  goals: Array<{ id: string; name: string }>;
  permissions: string[];
  sprints: Array<{ id: string; name: string; state: string }>;
  tasks: Task[];
};

const memberById = (members: Member[], id: string) => members.find((member) => member.id === id);

const PRIORITY_OPTIONS = PRIORITY_LEVELS.map((level) => ({ label: priorityMeta(level).label, value: level }));

const MERGE_STATUS_LABELS: Record<string, string> = {
  CONFLICTS: "merge conflicts",
  MERGEABLE: "mergeable",
  MERGED: "merged",
};
const pullRequestStatusLabel = (pullRequest: { mergeStatus: string | null; status: string }) =>
  (pullRequest.mergeStatus && MERGE_STATUS_LABELS[pullRequest.mergeStatus]) || pullRequest.status.toLowerCase();
// mergeStatus (when known) takes priority over the raw AWS status — a merged PR's
// own status is "CLOSED", but "merged" is the more useful color to show than the
// generic "closed" one, and a still-open PR's mergeability matters more than the
// fact that it's simply "open".
const pullRequestBadgeVariant = (pullRequest: { mergeStatus: string | null; status: string }) => {
  if (pullRequest.mergeStatus === "CONFLICTS") return "conflict";
  if (pullRequest.mergeStatus === "MERGEABLE") return "mergeable";
  if (pullRequest.mergeStatus === "MERGED") return "merged";
  if (pullRequest.status === "CLOSED") return "closed";
  if (pullRequest.status === "UNKNOWN") return "unknown";
  return "open";
};
const oneBusinessDayCutoff = (now: Date) => {
  const cutoff = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  cutoff.setDate(cutoff.getDate() - 1);
  const day = cutoff.getDay();
  if (day === 0) cutoff.setDate(cutoff.getDate() - 2);
  else if (day === 6) cutoff.setDate(cutoff.getDate() - 1);
  return cutoff;
};
const isStaleForScrum = (lastCommentAt: string | null, now = new Date()) =>
  !lastCommentAt || parseServerTime(lastCommentAt).getTime() < oneBusinessDayCutoff(now).getTime();

// Agent comments are tinted so people can tell them from human ones at a glance.
// Authored by the Coding Assistant account, or carrying the [claude-agent] marker
// (an agent working under a person's own token).
const isAgentAuthored = (comment: Pick<TaskComment, "authorId" | "body">) =>
  comment.authorId === CODING_ASSISTANT_USER_ID || isAgentComment(comment.body);

function LabelsField({ defaultLabels, name }: { defaultLabels: string[]; name: string }) {
  const [labels, setLabels] = useState<string[]>(defaultLabels);
  const [draft, setDraft] = useState("");
  const addLabel = () => {
    const value = draft.trim();
    if (value && !labels.includes(value)) setLabels((current) => [...current, value]);
    setDraft("");
  };
  const removeLabel = (label: string) => setLabels((current) => current.filter((item) => item !== label));
  return (
    <div className="label-field">
      <input name={name} readOnly type="hidden" value={labels.join(",")} />
      <div className="label-chips">
        {labels.map((label) => (
          <span className="label-chip" key={label}>
            {label}
            <button aria-label={`Remove label ${label}`} onClick={() => removeLabel(label)} type="button">×</button>
          </span>
        ))}
        <input
          aria-label="Add label"
          onBlur={addLabel}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === ",") { event.preventDefault(); addLabel(); }
            else if (event.key === "Backspace" && !draft && labels.length) removeLabel(labels[labels.length - 1]);
          }}
          placeholder={labels.length ? "" : "Add label"}
          value={draft}
        />
      </div>
    </div>
  );
}

const api = async <T,>(path: string, init?: RequestInit): Promise<T> => {
  const response = await fetch(path, {
    ...init,
    headers: init?.body
      ? { "content-type": "application/json", ...init.headers }
      : init?.headers,
  });
  const payload = (await response.json()) as T & { message?: string };
  if (!response.ok) throw new Error(payload.message || "Taskei could not complete that request.");
  return payload;
};

// Mirrors the server's CODECOMMIT_URL_PATTERN (tasks-api.ts) closely enough to
// route a review link to the right endpoint client-side — the server itself
// can't be imported into the browser bundle, and the two existing endpoints
// (link a PR vs. save a generic externalLinkUrl) need to be chosen between
// before either request goes out, not discovered by trial and error.
const looksLikeCodeCommitPullRequestUrl = (url: string) =>
  /^https:\/\/[a-z0-9-]+\.console\.aws\.amazon\.com\/codesuite\/codecommit\/repositories\/[^/]+\/pull-requests\/[^/?]+/i.test(url.trim());

// Shared by every "move into a review column" entry point (board drag-and-drop,
// the card's column picker, the backlog row's status select, and the task-detail
// sidebar's Status field) — see requestMove/TaskDetailsDialog.save() below.
const submitReviewEntryLink = async (taskId: string, link: string | null, displayName: string) => {
  if (link && looksLikeCodeCommitPullRequestUrl(link)) {
    await api(`/api/tasks/${taskId}/pull-requests`, { body: JSON.stringify({ url: link }), method: "POST" });
  } else if (link) {
    await api(`/api/tasks/${taskId}`, { body: JSON.stringify({ externalLinkUrl: link }), method: "PATCH" });
  } else {
    await api(`/api/tasks/${taskId}/comments`, {
      body: JSON.stringify({ body: wrapPlainTextAsDoc(`No PR or document to review — confirmed by ${displayName}.`) }),
      method: "POST",
    });
  }
};

// Asks for a PR/document link (or an explicit "there isn't one" confirmation)
// when a task enters a review-flagged column — modeled on ConfirmDialogView's
// shell (ui-kit.tsx) since useConfirm only supports a boolean/exact-text-match
// field, not a free-text URL input. Deliberately "dumb": it only collects the
// link and lets its caller (TaskWorkspace.requestMove or
// TaskDetailsDialog.save) own the busy/error state and what happens next,
// since the two callers need to do different things once the link step
// resolves (immediately move the task vs. proceed with saving other field
// changes too).
function ReviewEntryDialog({
  busy,
  columnName,
  displayName,
  error,
  onCancel,
  onSubmit,
}: {
  busy: boolean;
  columnName: string;
  displayName: string;
  error: string;
  onCancel: () => void;
  onSubmit: (link: string | null) => void;
}) {
  const dialogRef = useModalA11y<HTMLElement>(onCancel);
  const [link, setLink] = useState("");
  const hasLink = link.trim().length > 0;
  return (
    <div className="dialog-backdrop" onMouseDown={onCancel} role="presentation">
      <section aria-labelledby="review-entry-dialog-title" className="dialog review-entry-dialog" onMouseDown={(event) => event.stopPropagation()} ref={dialogRef} role="dialog" aria-modal="true">
        <button className="dialog-close" disabled={busy} onClick={onCancel} type="button">×</button>
        <p className="eyebrow">Moving to {columnName}</p>
        <h2 id="review-entry-dialog-title">Link a PR or document for review</h2>
        <p>Add the pull request or document being reviewed, or confirm there isn't one.</p>
        <form onSubmit={(event) => { event.preventDefault(); onSubmit(hasLink ? link.trim() : null); }}>
          <label>
            PR or document link
            <input autoFocus disabled={busy} onChange={(event) => setLink(event.target.value)} placeholder="https://…" type="url" value={link} />
          </label>
          <ErrorBanner>{error}</ErrorBanner>
          <div className="dialog-actions">
            <button className="button secondary" disabled={busy} onClick={onCancel} type="button">Cancel</button>
            <button className="button primary" disabled={busy} type="submit">
              {busy ? "Moving…" : hasLink ? "Move task" : `No PR or document to review — confirmed by ${displayName}`}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

// The move-into-review flow's second step (after ReviewEntryDialog's link
// step resolves) — any team member, multi-select, no exclusions, per the
// plan. Each toggle fires an immediate PATCH via onToggle (same
// immediate-on-select convention as everywhere else reviewers are editable),
// so there's no separate "save" action here — "Move task" both finishes
// picking reviewers and completes the move that's been paused this whole
// time. Picking zero reviewers is a valid, unblocked choice.
function ReviewerSelectDialog({
  busy,
  error,
  members,
  onDone,
  onToggle,
  selectedIds,
}: {
  busy: boolean;
  error: string;
  members: Member[];
  onDone: () => void;
  onToggle: (reviewerIds: string[]) => void;
  selectedIds: Set<string>;
}) {
  const dialogRef = useModalA11y<HTMLElement>(onDone);
  const memberNames = members.map((member) => member.displayName);
  return (
    <div className="dialog-backdrop" onMouseDown={onDone} role="presentation">
      <section aria-labelledby="reviewer-select-dialog-title" className="dialog reviewer-select-dialog" onMouseDown={(event) => event.stopPropagation()} ref={dialogRef} role="dialog" aria-modal="true">
        <button className="dialog-close" disabled={busy} onClick={onDone} type="button">×</button>
        <p className="eyebrow">Review</p>
        <h2 id="reviewer-select-dialog-title">Pick reviewers</h2>
        <p>Choose anyone on the team to review this — optional, and editable anytime from the task.</p>
        <div className="reviewer-select-list" role="group" aria-label="Reviewers">
          {members.map((member) => (
            <label className="reviewer-picker-row" key={member.id}>
              <input
                checked={selectedIds.has(member.id)}
                disabled={busy}
                onChange={(event) => {
                  const next = new Set(selectedIds);
                  if (event.target.checked) next.add(member.id); else next.delete(member.id);
                  onToggle([...next]);
                }}
                type="checkbox"
              />
              <span className="member-avatar"><AvatarContent allNames={memberNames} avatarUrl={member.avatarUrl} name={member.displayName} /></span>
              {member.displayName}
            </label>
          ))}
        </div>
        <ErrorBanner>{error}</ErrorBanner>
        <div className="dialog-actions">
          <button className="button primary" disabled={busy} onClick={onDone} type="button">{busy ? "Moving…" : "Move task"}</button>
        </div>
      </section>
    </div>
  );
}

function NewTaskDialog({
  columns,
  currentUserId,
  defaultAssigneeId,
  defaultSprintId,
  members,
  onClose,
  onCreated,
  sprints,
  teamId,
}: {
  columns: Column[];
  currentUserId: string;
  defaultAssigneeId?: string;
  defaultSprintId?: string | null;
  members: Member[];
  onClose: () => void;
  onCreated: (created: { assigneeId: string; id: string; sprintId: string | null; title: string; type: string }) => Promise<void>;
  sprints: TaskData["sprints"];
  teamId: string;
}) {
  const dialogRef = useModalA11y<HTMLElement>(onClose);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  // Description is controlled state, not read from FormData at submit like
  // the rest of this form -- the Tiptap editor doesn't participate in
  // native form submission the way a <textarea name="..."> did.
  const [description, setDescription] = useState<TiptapDoc | null>(null);
  const [titleTooLong, setTitleTooLong] = useState(false);
  const submit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = new FormData(event.currentTarget);
    if (String(form.get("title") ?? "").trim().length > MAX_TITLE_LENGTH) {
      setTitleTooLong(true);
      return;
    }
    setBusy(true);
    setError("");
    try {
      const created = await api<{ id: string }>("/api/tasks", {
        body: JSON.stringify({
          assigneeId: form.get("assigneeId"),
          columnId: form.get("columnId"),
          description,
          dueOn: form.get("dueOn") || null,
          estimatePoints: form.get("estimatePoints")
            ? Number(form.get("estimatePoints"))
            : null,
          priority: form.get("priority"),
          labels: String(form.get("labels") ?? "").split(",").map((label) => label.trim()).filter(Boolean),
          sprintId: form.get("sprintId") || null,
          startsOn: form.get("startsOn") || null,
          targetOn: form.get("targetOn") || null,
          teamId,
          title: form.get("title"),
          type: form.get("type"),
        }),
        method: "POST",
      });
      const pullRequestUrl = String(form.get("pullRequestUrl") ?? "").trim();
      if (pullRequestUrl) {
        await api(`/api/tasks/${created.id}/pull-requests`, { body: JSON.stringify({ url: pullRequestUrl }), method: "POST" });
      }
      await onCreated({
        assigneeId: String(form.get("assigneeId") ?? ""),
        id: created.id,
        sprintId: String(form.get("sprintId") ?? "") || null,
        title: String(form.get("title") ?? ""),
        type: String(form.get("type") ?? "task"),
      });
    } catch (reason) {
      setBusy(false);
      setError(reason instanceof Error ? reason.message : "Could not create task.");
    }
  };
  return (
    <div className="dialog-backdrop" onMouseDown={onClose} role="presentation">
      <section className="dialog task-dialog" onMouseDown={(event) => event.stopPropagation()} ref={dialogRef} role="dialog" aria-modal="true">
        <button className="dialog-close" onClick={onClose} type="button">×</button>
        <p className="eyebrow">Team backlog</p>
        <h2>Create task</h2>
        <form onSubmit={submit}>
          <label className="task-title-field">
            <span>Title<RequiredMark /></span>
            <input
              aria-describedby={titleTooLong ? "new-task-title-error" : undefined}
              aria-invalid={titleTooLong || undefined}
              autoFocus
              name="title"
              onChange={(event) => setTitleTooLong(event.target.value.trim().length > MAX_TITLE_LENGTH)}
              placeholder="What needs to be done?"
              required
            />
            {titleTooLong && <span className="field-error" id="new-task-title-error" role="alert">Title must be {MAX_TITLE_LENGTH} characters or fewer.</span>}
          </label>
          <label htmlFor="new-task-description">
            Description
            <RichTextEditor
              ariaLabel="New task description"
              characterLimit={10000}
              members={members}
              onChange={setDescription}
              placeholder="Context, expected outcome, or acceptance notes — type @ to mention someone"
              value={description}
            />
          </label>
          <div className="task-form-grid">
            <label>
              Type
              <select aria-label="New task type" name="type" defaultValue="task">
                <option value="epic">Epic</option>
                <option value="story">Story</option>
                <option value="task">Task</option>
                <option value="subtask">Subtask</option>
                <option value="bug">Bug</option>
              </select>
            </label>
            <label>
              <span>Assignee<RequiredMark /></span>
              <select defaultValue={defaultAssigneeId ?? currentUserId} name="assigneeId" required>
                {members.map((member) => <option key={member.id} value={member.id}>{member.displayName}</option>)}
              </select>
            </label>
            <label>
              Priority
              <select name="priority" defaultValue="medium">
                {PRIORITY_OPTIONS.map((option) => <option key={option.value} value={option.value}>{option.label}</option>)}
              </select>
            </label>
            <label>
              Estimate
              <input min="0" name="estimatePoints" placeholder="Points" step="0.25" type="number" />
            </label>
            <label>
              Status
              <select name="columnId">
                {columns.map((column) => <option key={column.id} value={column.id}>{column.name}</option>)}
              </select>
            </label>
            <label>
              Sprint
              <select name="sprintId" defaultValue={defaultSprintId ?? ""}>
                <option value="">Backlog</option>
                {sprints.filter(({ state }) => ["planned", "active"].includes(state)).map((sprint) => (
                  <option key={sprint.id} value={sprint.id}>{sprint.name}</option>
                ))}
              </select>
            </label>
            <label>
              Due date
              <input name="dueOn" type="date" />
            </label>
            <label>
              <span>Planned start</span>
              <input name="startsOn" type="date" />
            </label>
            <label>
              <span>Planned target</span>
              <input name="targetOn" type="date" />
            </label>
            <label>
              Labels
              <LabelsField defaultLabels={[]} name="labels" />
            </label>
            <label className="task-detail-pr-field">
              Pull request URL
              <input name="pullRequestUrl" placeholder="https://<region>.console.aws.amazon.com/codesuite/codecommit/repositories/<repo>/pull-requests/<id>/details" type="url" />
            </label>
          </div>
          <ErrorBanner>{error}</ErrorBanner>
          <div className="dialog-actions">
            <button className="button secondary" onClick={onClose} type="button">Cancel</button>
            <button className="button primary" disabled={busy} type="submit">{busy ? "Creating…" : "Create task"}</button>
          </div>
        </form>
      </section>
    </div>
  );
}

function TaskCard({
  columns,
  isReorderTarget,
  members,
  onAssigneeChange,
  onCardDragEnter,
  onCardDrop,
  onDragStart,
  onDragEnd,
  onEstimateChange,
  onMove,
  onOpen,
  onPriorityChange,
  onRefreshPullRequest,
  onReviewersChange,
  task,
}: {
  columns: Column[];
  isReorderTarget?: boolean;
  members: Member[];
  onAssigneeChange: (task: Task, assigneeId: string) => Promise<void>;
  onCardDragEnter?: () => void;
  onCardDrop?: () => void;
  onDragStart: (taskId: string) => void;
  onDragEnd: () => void;
  onEstimateChange: (task: Task, estimatePoints: number | null) => Promise<void>;
  onMove: (task: Task, column: Column) => Promise<void>;
  onOpen: (task: Task) => void;
  onPriorityChange: (task: Task, priority: string) => Promise<void>;
  onRefreshPullRequest: (task: Task, pullRequestId: string) => Promise<void>;
  onReviewersChange: (task: Task, reviewerIds: string[]) => Promise<void>;
  task: Task;
}) {
  const assignee = memberById(members, task.assigneeId);
  const memberNames = members.map((member) => member.displayName);
  const [refreshingPullRequestId, setRefreshingPullRequestId] = useState<string | null>(null);
  const [columnPickerOpen, setColumnPickerOpen] = useState(false);
  const [reviewersBusy, setReviewersBusy] = useState(false);
  const currentColumn = columns.find(({ id }) => id === task.columnId);
  const typeLabel = epicParentLabel(task) ?? issueTypeMeta(task.type).label;
  return (
    <article
      aria-label={`${task.taskKey}: ${task.title}`}
      className={`task-card${isReorderTarget ? " reorder-target" : ""}`}
      draggable
      onDragEnd={onDragEnd}
      onDragEnter={onCardDragEnter && ((event: DragEvent) => { event.preventDefault(); event.stopPropagation(); onCardDragEnter(); })}
      onDragOver={onCardDrop && ((event: DragEvent) => { event.preventDefault(); event.stopPropagation(); })}
      onDragStart={() => onDragStart(task.id)}
      onDrop={onCardDrop && ((event: DragEvent) => { event.preventDefault(); event.stopPropagation(); onCardDrop(); })}
    >
      <h3>{task.title}</h3>
      {task.labels.length > 0 && (
        <div className="task-card-labels">
          {task.labels.map((label) => <span className="label-chip" key={label}>{label}</span>)}
        </div>
      )}
      <div className="task-card-meta">
        <IssueTypeIcon title={typeLabel} type={task.type} />
        <span className="sr-only">{typeLabel}</span>
        <span className="task-card-key"><span aria-hidden="true">▣</span><strong>{task.taskKey}</strong></span>
        {epicParentLabel(task) && <span className="epic-chip">{epicParentLabel(task)}</span>}
        <span className="task-card-priority" onClick={(event) => event.stopPropagation()}>
          <FieldPicker
            ariaLabel={`Change priority for ${task.taskKey} (currently ${priorityMeta(task.priority).label})`}
            items={PRIORITY_OPTIONS.map((option) => ({ id: option.value, label: option.label }))}
            name="priority"
            onSelect={(priority) => void onPriorityChange(task, priority)}
            renderLabel={(option) => <><PriorityIcon priority={option.id} />{option.label}</>}
            renderTrigger={<PriorityIcon priority={task.priority} />}
            selectedId={task.priority}
          />
          <PointsEditor
            ariaLabel={`Change story points for ${task.taskKey} (currently ${task.estimatePoints ?? "unestimated"})`}
            onChange={(estimatePoints) => void onEstimateChange(task, estimatePoints)}
            value={task.estimatePoints}
          />
        </span>
        <span className="task-card-assignee" onClick={(event) => event.stopPropagation()}>
          <FieldPicker
            ariaLabel={`Change assignee for ${task.taskKey} (currently ${task.assigneeName ?? "Unassigned"})`}
            items={members}
            name="assigneeId"
            onSelect={(assigneeId) => void onAssigneeChange(task, assigneeId)}
            renderLabel={(member) => <><span className="member-avatar"><AvatarContent allNames={memberNames} avatarUrl={member.avatarUrl} name={member.displayName} /></span>{member.displayName}</>}
            renderTrigger={<span className="member-avatar" title={task.assigneeName}><AvatarContent allNames={memberNames} avatarUrl={assignee?.avatarUrl} name={task.assigneeName} /></span>}
            selectedId={task.assigneeId}
          />
        </span>
        <span className="column-picker">
          <button
            aria-expanded={columnPickerOpen}
            aria-label={`Move ${task.taskKey}${currentColumn ? ` (currently ${currentColumn.name})` : ""}`}
            className="column-picker-trigger"
            onClick={(event) => { event.stopPropagation(); setColumnPickerOpen((value) => !value); }}
            title="Move to a different column"
            type="button"
          >
            <i aria-hidden="true" className="bi bi-arrow-right-circle" />
          </button>
          {columnPickerOpen && (
            <>
              <div className="type-picker-backdrop" onClick={(event) => { event.stopPropagation(); setColumnPickerOpen(false); }} />
              <div className="type-picker-menu column-picker-menu" role="menu">
                {columns.map((column) => (
                  <button
                    aria-current={column.id === task.columnId}
                    key={column.id}
                    onClick={(event) => { event.stopPropagation(); setColumnPickerOpen(false); void onMove(task, column); }}
                    role="menuitem"
                    type="button"
                  >
                    {column.name}
                  </button>
                ))}
              </div>
            </>
          )}
        </span>
      </div>
      {currentColumn?.isReview ? (
        <div className="task-card-reviewers" onClick={(event) => event.stopPropagation()}>
          <ReviewerPickerField
            ariaLabel={`Edit reviewers for ${task.taskKey} (${task.reviewers.length} selected)`}
            busy={reviewersBusy}
            members={members}
            onChange={(reviewerIds) => {
              setReviewersBusy(true);
              void onReviewersChange(task, reviewerIds).finally(() => setReviewersBusy(false));
            }}
            reviewerIds={new Set(task.reviewers.map((reviewer) => reviewer.reviewerId))}
            reviewers={task.reviewers}
          />
        </div>
      ) : null}
      {task.pullRequests.length > 0 && (
        <div className="task-card-pull-requests" aria-label="Linked pull requests">
          {task.pullRequests.map((pullRequest) => {
            const activity = relativeTimeFrom(pullRequest.lastActivityAt);
            const refreshing = refreshingPullRequestId === pullRequest.pullRequestId;
            return (
              <span
                className={`pull-request-badge pull-request-badge-${pullRequestBadgeVariant(pullRequest)}`}
                key={`${pullRequest.repositoryName}-${pullRequest.pullRequestId}`}
              >
                <a href={pullRequest.url} rel="noreferrer" target="_blank" title={pullRequest.title}>
                  PR #{pullRequest.pullRequestId} · {pullRequestStatusLabel(pullRequest)}{activity ? ` · ${activity}` : ""}
                </a>
                {pullRequest.approvalCount !== null && (
                  <span
                    className="pull-request-approvals"
                    title={`${pullRequest.approvalCount} approval${pullRequest.approvalCount === 1 ? "" : "s"}`}
                  >
                    <i aria-hidden="true" className="bi bi-check2-circle" />{pullRequest.approvalCount}
                  </span>
                )}
                {pullRequest.errorMessage && (
                  <span className="pull-request-sync-warning" title={`Could not refresh from AWS CodeCommit: ${pullRequest.errorMessage}`}>
                    <i aria-hidden="true" className="bi bi-exclamation-triangle-fill" />
                  </span>
                )}
                <button
                  aria-label={`Refresh status for PR #${pullRequest.pullRequestId}`}
                  className="pull-request-refresh"
                  disabled={refreshing}
                  onClick={async (event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    setRefreshingPullRequestId(pullRequest.pullRequestId);
                    try { await onRefreshPullRequest(task, pullRequest.pullRequestId); }
                    finally { setRefreshingPullRequestId(null); }
                  }}
                  title="Refresh pull request status"
                  type="button"
                >
                  <i aria-hidden="true" className={`bi bi-arrow-repeat${refreshing ? " spin" : ""}`} />
                </button>
              </span>
            );
          })}
        </div>
      )}
      <button className="task-card-open" onClick={() => onOpen(task)} type="button"><span className="sr-only">Open details</span></button>
    </article>
  );
}

// Options for the sidebar's "Parent task" picker: only Epics, since every
// other type nests under one -- listing every task here (as this used to)
// let a story get "parented" under an unrelated task/bug with no hierarchy
// meaning at all. Exported and pure (no React) so it's unit-testable without
// a DOM environment, per this file's existing test conventions.
export const parentTaskOptions = <T extends { id: string; taskKey: string; title: string; type: string }>(
  tasks: T[],
  excludeId: string,
): Array<{ id: string; label: string }> => [
  { id: "", label: "No parent" },
  ...tasks
    .filter((candidate) => candidate.id !== excludeId && candidate.type === "epic")
    .map((candidate) => ({ id: candidate.id, label: `${candidate.taskKey} · ${candidate.title}` })),
];

// The backlog row's parent chip used to show the literal string "Parent
// work item" whenever a task had ANY parent at all, with no way to tell
// which epic (or that it even had one) without opening the task -- unlike
// Jira, which names the epic right there in the list next to the item's own
// type. Resolves `task`'s parent's "KEY · Title" label whenever that parent
// is genuinely an Epic (the only kind of parent a task can have, per
// parentTaskOptions above), returning null otherwise so the caller falls
// back to just the item's own type.
//
// Reads `epicKey`/`epicTitle` straight off the task -- listTasks/loadTaskById
// resolve them server-side with a correlated subquery keyed on the row's own
// parent_work_item_id (see tasks-api.ts), independent of whatever
// query/type/assigneeId filters narrowed the *rest* of the response. An
// earlier version instead looked the parent up in the current page's task
// list, which silently went blank the moment a filter (e.g. the assignee
// filter, since epics are so often unassigned) filtered the epic itself out
// of that list. Pure/exported for the same reason as parentTaskOptions above.
export const epicParentLabel = (task: { epicKey?: string | null; epicTitle?: string | null }): string | null =>
  task.epicKey && task.epicTitle ? `${task.epicKey} · ${task.epicTitle}` : null;

// Ids of everyone with at least one task in `tasks` (already scoped to a sprint).
export const sprintAssigneeIds = (tasks: Array<{ assigneeId?: string | null }>): Set<string> =>
  new Set(tasks.map((task) => task.assigneeId).filter((id): id is string => Boolean(id)));

// Which members get an avatar chip in the assignee filter. Only the board with
// a sprint selected narrows to members who have work in that sprint, and only
// when that set is known (`sprintAssigneeIds` null = not known yet, so show
// everyone rather than guess). The currently selected assignee is always kept
// so the active filter can be toggled off. Pure/exported for unit testing.
export const assigneeFilterCandidates = <M extends { id: string }>(
  members: M[],
  options: { mode: string; selectedId: string; sprintAssigneeIds: ReadonlySet<string> | null; sprintId: string | null | undefined },
): M[] => {
  if (options.mode !== "board" || !options.sprintId || !options.sprintAssigneeIds) return members;
  const known = options.sprintAssigneeIds;
  return members.filter((member) => known.has(member.id) || member.id === options.selectedId);
};

// The search-filter used by FieldPicker's `searchable` mode: a
// case-insensitive substring match against each item's plain-text label.
// Pure/exported for the same reason as parentTaskOptions above.
export const filterPickerItems = <T,>(
  items: T[],
  query: string,
  getSearchText: (item: T) => string,
): T[] => {
  const trimmed = query.trim().toLowerCase();
  if (!trimmed) return items;
  return items.filter((item) => getSearchText(item).toLowerCase().includes(trimmed));
};

// A Jira-style "click to open a menu" field control, replacing what used to be
// a bare native <select> — reuses the same trigger-button + menu shape already
// established for the type-picker and assignee-filter overflow menu, so every
// sidebar field looks and behaves consistently. A hidden input still carries
// the selected id under `name`, so the surrounding <form>'s FormData-based
// save() needs no changes at all. Closes on outside click via
// useClickOutsideToClose rather than a full-viewport backdrop div — see that
// hook's comment for why (a backdrop swallowed clicks/scrolls anywhere else
// in the app while a picker was left open).
//
// `searchable` opts a picker into a text filter at the top of its menu (the
// Parent-task picker needs this — a plain click-through list doesn't scale
// once a team has more than a handful of Epics). `getSearchText` supplies the
// plain-text label each item is matched against; required when `searchable`
// is true, since `renderLabel` returns a ReactNode a query can't be matched
// against directly.
export function FieldPicker<T extends { id: string }>({
  ariaLabel, getSearchText, items, name, onSelect, renderLabel, renderTrigger, searchAriaLabel, searchable, selectedId,
}: {
  ariaLabel: string;
  getSearchText?: (item: T) => string;
  items: T[];
  name: string;
  onSelect: (id: string) => void;
  renderLabel: (item: T) => ReactNode;
  renderTrigger: ReactNode;
  searchAriaLabel?: string;
  searchable?: boolean;
  selectedId: string;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const close = () => { setOpen(false); setQuery(""); };
  const ref = useClickOutsideToClose<HTMLSpanElement>(open, close);
  const visibleItems = searchable ? filterPickerItems(items, query, getSearchText ?? (() => "")) : items;
  return (
    <span className="field-picker" ref={ref}>
      <input name={name} type="hidden" value={selectedId} />
      <button aria-expanded={open} aria-label={ariaLabel} className="field-picker-trigger" onClick={() => setOpen((value) => !value)} type="button">
        {renderTrigger}
        <i aria-hidden="true" className="bi bi-chevron-down field-picker-chevron" />
      </button>
      {open && (
        <div className="type-picker-menu field-picker-menu" role="menu">
          {searchable && (
            <input
              aria-label={searchAriaLabel ?? "Search"}
              autoFocus
              className="field-picker-search"
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Type to search…"
              type="text"
              value={query}
            />
          )}
          {visibleItems.length
            ? visibleItems.map((item) => (
              <button aria-current={item.id === selectedId} key={item.id || "none"} onClick={() => { onSelect(item.id); close(); }} role="menuitem" type="button">
                {renderLabel(item)}
              </button>
            ))
            : <p className="field-picker-empty">No matches</p>}
        </div>
      )}
    </span>
  );
}

// Click-to-edit story points, replacing what used to be a static read-only
// badge on the board card and backlog row (points could previously only be
// changed by opening the full task-detail dialog). A menu-based FieldPicker
// doesn't fit a free-form number, so this is its own lighter control: the
// badge itself is the trigger, swapping to a small number input on click,
// committing on Enter/blur (immediate PATCH, same convention as every other
// inline field here) and discarding the edit on Escape.
function PointsEditor({ ariaLabel, onChange, value }: {
  ariaLabel: string; onChange: (points: number | null) => void; value: number | null;
}) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value === null ? "" : String(value));
  const cancelledRef = useRef(false);
  useEffect(() => setDraft(value === null ? "" : String(value)), [value]);
  const commit = () => {
    setEditing(false);
    if (cancelledRef.current) { cancelledRef.current = false; return; }
    const trimmed = draft.trim();
    if (!trimmed) { if (value !== null) onChange(null); return; }
    const next = Number(trimmed);
    if (!Number.isFinite(next) || next < 0) { setDraft(value === null ? "" : String(value)); return; }
    if (next !== value) onChange(next);
  };
  if (editing) {
    return (
      <input
        aria-label={ariaLabel}
        autoFocus
        className="points-input"
        min="0"
        onBlur={commit}
        onChange={(event) => setDraft(event.target.value)}
        onFocus={(event) => event.currentTarget.select()}
        onKeyDown={(event) => {
          if (event.key === "Enter") { event.preventDefault(); event.currentTarget.blur(); }
          else if (event.key === "Escape") {
            event.preventDefault();
            cancelledRef.current = true;
            setDraft(value === null ? "" : String(value));
            event.currentTarget.blur();
          }
        }}
        step="0.25"
        type="number"
        value={draft}
      />
    );
  }
  return (
    <button aria-label={ariaLabel} className="points-badge points-badge-editable" onClick={() => setEditing(true)} type="button">
      {value ?? "–"}
    </button>
  );
}

// The reviewer-editing control: a compact stacked-avatar trigger (or "No
// reviewers" text when none are picked) that opens a checkbox-list popover —
// any team member, multi-select, no exclusions, per the plan. Shared between
// the task-detail sidebar's "Reviewers" field, the board card (shown only
// while the card sits in a review-flagged column), and the move-into-review
// flow's own reviewer step below, so "clicking opens the same popover used
// to edit reviewers" holds everywhere rather than needing three variants.
// Immediate-PATCH-on-toggle, matching FieldPicker's onSelect convention.
function ReviewerPickerField({
  ariaLabel,
  busy,
  members,
  onChange,
  reviewerIds,
  reviewers,
}: {
  ariaLabel: string;
  busy?: boolean;
  members: Member[];
  onChange: (reviewerIds: string[]) => void;
  reviewerIds: Set<string>;
  reviewers: Task["reviewers"];
}) {
  const [open, setOpen] = useState(false);
  const ref = useClickOutsideToClose<HTMLSpanElement>(open, () => setOpen(false));
  const memberNames = members.map((member) => member.displayName);
  return (
    <span className="field-picker reviewer-picker" ref={ref}>
      <button aria-expanded={open} aria-label={ariaLabel} className="field-picker-trigger reviewer-picker-trigger" disabled={busy} onClick={() => setOpen((value) => !value)} type="button">
        {reviewers.length ? (
          <span className="reviewer-avatar-stack">
            {reviewers.map((reviewer) => (
              <span
                className={`member-avatar reviewer-avatar reviewer-avatar-${reviewer.status}`}
                key={reviewer.reviewerId}
                title={`${reviewer.displayName} — ${reviewer.status === "approved" ? `approved${reviewer.approvedAt ? ` ${new Date(reviewer.approvedAt).toLocaleString()}` : ""}` : "pending"}`}
              >
                <AvatarContent allNames={memberNames} avatarUrl={reviewer.avatarUrl} name={reviewer.displayName} />
              </span>
            ))}
          </span>
        ) : <span className="reviewer-picker-empty">No reviewers</span>}
        <i aria-hidden="true" className="bi bi-chevron-down field-picker-chevron" />
      </button>
      {open && (
        <div className="type-picker-menu reviewer-picker-menu" role="group" aria-label="Reviewers">
          {members.map((member) => (
            <label className="reviewer-picker-row" key={member.id}>
              <input
                checked={reviewerIds.has(member.id)}
                disabled={busy}
                onChange={(event) => {
                  const next = new Set(reviewerIds);
                  if (event.target.checked) next.add(member.id); else next.delete(member.id);
                  onChange([...next]);
                }}
                type="checkbox"
              />
              <span className="member-avatar"><AvatarContent allNames={memberNames} avatarUrl={member.avatarUrl} name={member.displayName} /></span>
              {member.displayName}
            </label>
          ))}
        </div>
      )}
    </span>
  );
}

const formatFileSize = (bytes: number) => bytes < 1024 * 1024
  ? `${Math.max(1, Math.round(bytes / 1024))} KB`
  : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

function AttachFileButton({ busy, onSelect }: { busy: boolean; onSelect: (file: File) => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  return (
    <>
      <button className="button secondary" disabled={busy} onClick={() => inputRef.current?.click()} type="button">
        <i aria-hidden="true" className="bi bi-paperclip" /> {busy ? "Uploading…" : "Attach file"}
      </button>
      <input
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = "";
          if (file) onSelect(file);
        }}
        ref={inputRef}
        type="file"
      />
    </>
  );
}

// Files uploaded (to S3) but not yet submitted with the comment they'll
// belong to -- "remove" here only un-stages the metadata, matching the
// accepted limitation noted in the plan (the S3 object itself isn't cleaned
// up if the compose form is abandoned; a small internal tool doesn't
// warrant building orphan-sweep infrastructure for that edge case).
function PendingAttachmentList({ attachments, onRemove }: { attachments: PendingAttachment[]; onRemove: (attachmentId: string) => void }) {
  if (!attachments.length) return null;
  return (
    <ul className="attachment-chip-list">
      {attachments.map((attachment) => (
        <li className="attachment-chip" key={attachment.attachmentId}>
          <i aria-hidden="true" className="bi bi-file-earmark" />
          <span>{attachment.fileName}</span>
          <span className="attachment-chip-size">{formatFileSize(attachment.sizeBytes)}</span>
          <button aria-label={`Remove ${attachment.fileName}`} onClick={() => onRemove(attachment.attachmentId)} type="button">×</button>
        </li>
      ))}
    </ul>
  );
}

function ExistingAttachmentList({
  attachments, canDelete, commentId, deletingAttachmentId, onDelete, taskId,
}: {
  attachments: CommentAttachment[]; canDelete: boolean; commentId: string;
  deletingAttachmentId: string | null; onDelete: (attachmentId: string) => void; taskId: string;
}) {
  return (
    <ul className="attachment-chip-list">
      {attachments.map((attachment) => (
        <li className="attachment-chip" key={attachment.id}>
          <a
            href={`/api/tasks/${taskId}/comments/${commentId}/attachments/${attachment.id}/download`}
            rel="noreferrer"
            target="_blank"
          >
            <i aria-hidden="true" className="bi bi-file-earmark-arrow-down" /> {attachment.fileName}
          </a>
          <span className="attachment-chip-size">{formatFileSize(attachment.sizeBytes)}</span>
          {canDelete && (
            <button
              aria-label={`Delete ${attachment.fileName}`}
              disabled={deletingAttachmentId === attachment.id}
              onClick={() => onDelete(attachment.id)}
              type="button"
            >
              ×
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

function TaskDetailsDialog({
  canDelete,
  columns,
  currentUserId,
  goals,
  members,
  onClose,
  onDeleted,
  onUpdated,
  sprints,
  task,
  taskUrl,
  tasks,
}: {
  canDelete: boolean;
  columns: Column[];
  currentUserId: string;
  goals: TaskData["goals"];
  members: Member[];
  onClose: () => void;
  onDeleted: () => Promise<void>;
  onUpdated: (task: Task) => void;
  sprints: TaskData["sprints"];
  task: Task;
  taskUrl: string;
  tasks: Task[];
}) {
  const dialogRef = useModalA11y<HTMLElement>(onClose);
  const confirm = useConfirm();
  const showToast = useToast();
  const memberNames = members.map((member) => member.displayName);
  const [busy, setBusy] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [comments, setComments] = useState<TaskComment[]>([]);
  const [commentBody, setCommentBody] = useState<TiptapDoc | null>(null);
  const [answerBusy, setAnswerBusy] = useState(false);
  const [commentAttachments, setCommentAttachments] = useState<PendingAttachment[]>([]);
  const [dependencies, setDependencies] = useState<{ blockers: DependencyTask[]; blocking: DependencyTask[] }>({ blockers: [], blocking: [] });
  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const [editingCommentBody, setEditingCommentBody] = useState<TiptapDoc | null>(null);
  const [editingCommentAttachments, setEditingCommentAttachments] = useState<PendingAttachment[]>([]);
  const [attachmentUploadBusy, setAttachmentUploadBusy] = useState(false);
  const [attachmentUploadError, setAttachmentUploadError] = useState("");
  const [deletingAttachmentId, setDeletingAttachmentId] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [pullRequestUrl, setPullRequestUrl] = useState("");
  const [pullRequestBusy, setPullRequestBusy] = useState(false);
  const [refreshingPullRequestId, setRefreshingPullRequestId] = useState<string | null>(null);
  const [unlinkingPullRequestId, setUnlinkingPullRequestId] = useState<string | null>(null);
  const [typePickerOpen, setTypePickerOpen] = useState(false);
  const typePickerRef = useClickOutsideToClose<HTMLSpanElement>(typePickerOpen, () => setTypePickerOpen(false));
  const [reviewersBusy, setReviewersBusy] = useState(false);
  const [pendingReviewEntry, setPendingReviewEntry] = useState<{ busy: boolean; error: string; form: FormData; step: "link" | "reviewers"; targetColumnId: string } | null>(null);
  // Each FieldPicker below is a controlled button+menu, not an uncontrolled
  // native <select> — its current choice has to live in state so the trigger's
  // display updates immediately on selection, with a hidden input still
  // carrying the value into the form's own FormData on save. Reset whenever a
  // different task's dialog reuses this component instance.
  const [assigneeId, setAssigneeId] = useState(task.assigneeId);
  const [priority, setPriority] = useState(task.priority);
  const [columnId, setColumnId] = useState(task.columnId);
  const [sprintId, setSprintId] = useState(task.sprintId ?? "");
  const [parentId, setParentId] = useState(task.parentId ?? "");
  const [goalId, setGoalId] = useState(task.goalId ?? "");
  const [activeTab, setActiveTab] = useState<"dependencies" | "details" | "history">("details");
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  // Title/description are controlled (unlike most of this form, which reads
  // FormData at submit) purely so "Refine with AI" can push an accepted
  // suggestion into the visible fields immediately, the same way accepting
  // any other AI-generated change would show up right away elsewhere in the
  // app -- see acceptAiRefine below.
  const [title, setTitle] = useState(task.title);
  const [description, setDescription] = useState<TiptapDoc | null>(task.description);
  // A task that already has a description opens straight into its rendered
  // preview (matching Jira, which always shows formatted content first) --
  // only a still-empty description starts directly in the editable view,
  // since there's nothing yet to render. Reusing the same
  // rendered-view-then-"Edit" pattern already used for comments below
  // (RichTextView + an Edit button), rather than dropping the reader
  // straight into an editable field just to read what's already there.
  const [editingDescription, setEditingDescription] = useState(!docToPlainText(task.description).trim());
  const [aiRefine, setAiRefine] = useState<{
    busy: boolean;
    current: { description: TiptapDoc | null; title: string };
    error: string;
    suggested: { description: TiptapDoc | null; title: string } | null;
  } | null>(null);
  useEffect(() => {
    setAssigneeId(task.assigneeId);
    setPriority(task.priority);
    setColumnId(task.columnId);
    setSprintId(task.sprintId ?? "");
    setParentId(task.parentId ?? "");
    setGoalId(task.goalId ?? "");
    setActiveTab("details");
    setTitle(task.title);
    setDescription(task.description);
    setEditingDescription(!docToPlainText(task.description).trim());
    setAiRefine(null);
  }, [task.id]);
  const loadComments = useCallback(async () => {
    const result = await api<{ comments: TaskComment[] }>(`/api/tasks/${task.id}/comments`);
    setComments(result.comments);
  }, [task.id]);
  const loadDependencies = useCallback(async () => {
    const result = await api<{ blockers: DependencyTask[]; blocking: DependencyTask[] }>(`/api/tasks/${task.id}/dependencies`);
    setDependencies(result);
  }, [task.id]);
  const loadHistory = useCallback(async () => {
    const result = await api<{ history: HistoryEntry[] }>(`/api/tasks/${task.id}/history`);
    setHistory(result.history);
  }, [task.id]);
  useEffect(() => {
    void Promise.all([loadComments(), loadDependencies(), loadHistory()]).catch(() => setError("Could not load task activity."));
  }, [loadComments, loadDependencies, loadHistory]);

  const performSave = async (form: FormData) => {
    setBusy(true); setError("");
    try {
      const response = await api<{ task: Task | null }>(`/api/tasks/${task.id}`, {
        body: JSON.stringify({
          assigneeId: form.get("assigneeId"),
          columnId: form.get("columnId"),
          description,
          dueOn: form.get("dueOn") || null,
          estimatePoints: form.get("estimatePoints") ? Number(form.get("estimatePoints")) : null,
          externalLinkUrl: form.get("externalLinkUrl") || null,
          labels: String(form.get("labels") ?? "").split(",").map((label) => label.trim()).filter(Boolean),
          parentId: form.get("parentId") || null,
          priority: form.get("priority"),
          sprintId: form.get("sprintId") || null,
          startsOn: form.get("startsOn") || null,
          targetOn: form.get("targetOn") || null,
          title: form.get("title"),
        }),
        method: "PATCH",
      });
      // Goal linking is deliberately a separate call, not part of the task
      // PATCH body -- goal_work_items is goals-api.ts's table, and keeping
      // tasks-api.ts free of goals knowledge is the same per-module
      // independence this codebase already applies to sprints/board/etc.
      // The task drawer only ever shows/sets a single linked goal (a
      // simplification for ease of use); linking the same item to several
      // goals at once is still possible from the Goals page itself.
      const nextGoalId = String(form.get("goalId") ?? "");
      if (nextGoalId !== (task.goalId ?? "")) {
        if (task.goalId) await api(`/api/goals/${task.goalId}/links/${task.id}`, { method: "DELETE" });
        if (nextGoalId) await api(`/api/goals/${nextGoalId}/links`, { body: JSON.stringify({ workItemId: task.id }), method: "POST" });
      }
      if (response.task) onUpdated({ ...response.task, goalId: nextGoalId || null });
      onClose();
    } catch (reason) {
      setBusy(false);
      setError(reason instanceof Error ? reason.message : "Could not update task.");
    }
  };
  const titleTooLong = title.trim().length > MAX_TITLE_LENGTH;
  const save = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (titleTooLong) return;
    const form = new FormData(event.currentTarget);
    // Same "genuine entry into review" check as TaskWorkspace.requestMove —
    // this is a separate, structurally different path (batched into one
    // form submit, not moveTask), so it needs its own diff check: only fire
    // the prompt when Status is actually changing away from a non-review
    // column into a review-flagged one, not on every save. Skip the prompt
    // when a link already exists (or is being added in this same save).
    const targetColumnId = String(form.get("columnId") ?? "");
    const targetIsReview = Boolean(columns.find(({ id }) => id === targetColumnId)?.isReview);
    const currentIsReview = Boolean(columns.find(({ id }) => id === task.columnId)?.isReview);
    const hasLink = Boolean(task.externalLinkUrl) || task.pullRequests.length > 0 || Boolean(form.get("externalLinkUrl"));
    if (targetColumnId !== task.columnId && targetIsReview && !currentIsReview && !hasLink) {
      setPendingReviewEntry({ busy: false, error: "", form, step: "link", targetColumnId });
      return;
    }
    await performSave(form);
  };
  const submitPendingReviewEntry = async (link: string | null) => {
    if (!pendingReviewEntry) return;
    setPendingReviewEntry((current) => (current ? { ...current, busy: true, error: "" } : current));
    try {
      await submitReviewEntryLink(task.id, link, memberById(members, currentUserId)?.displayName ?? "a teammate");
      setPendingReviewEntry((current) => (current ? { ...current, busy: false, step: "reviewers" } : current));
    } catch (reason) {
      setPendingReviewEntry((current) => (current
        ? { ...current, busy: false, error: reason instanceof Error ? reason.message : "Could not complete the move." }
        : current));
    }
  };
  const setPendingReviewEntryReviewers = async (reviewerIds: string[]) => {
    if (!pendingReviewEntry) return;
    setPendingReviewEntry((current) => (current ? { ...current, busy: true, error: "" } : current));
    try {
      const response = await api<{ task: Task | null }>(`/api/tasks/${task.id}/reviewers`, { body: JSON.stringify({ reviewerIds }), method: "PUT" });
      if (response.task) onUpdated(response.task);
      setPendingReviewEntry((current) => (current ? { ...current, busy: false } : current));
    } catch (reason) {
      setPendingReviewEntry((current) => (current
        ? { ...current, busy: false, error: reason instanceof Error ? reason.message : "Could not update reviewers." }
        : current));
    }
  };
  const finishPendingReviewEntry = async () => {
    if (!pendingReviewEntry) return;
    const { form } = pendingReviewEntry;
    setPendingReviewEntry(null);
    await performSave(form);
  };
  // Uploads a discrete (non-image) file attachment immediately on selection
  // -- before the comment it'll belong to is even submitted -- and stages
  // its metadata locally; the comment create/edit call below is what
  // actually registers it (see parseAttachmentInputs in tasks-api.ts). The
  // file itself is already safely in S3 the moment this resolves, so
  // abandoning the compose form just leaves an orphaned object, not a
  // half-written comment.
  const uploadFileAttachment = async (file: File): Promise<PendingAttachment | null> => {
    setAttachmentUploadBusy(true); setAttachmentUploadError("");
    try {
      const presign = await api<{ attachmentId: string; key: string; uploadUrl: string }>(`/api/tasks/${task.id}/attachments/presign`, {
        body: JSON.stringify({ contentType: file.type || "application/octet-stream", fileName: file.name, sizeBytes: file.size }),
        method: "POST",
      });
      const uploadResponse = await fetch(presign.uploadUrl, { body: file, method: "PUT" });
      if (!uploadResponse.ok) throw new Error("Could not upload the file.");
      return {
        attachmentId: presign.attachmentId, contentType: file.type || "application/octet-stream",
        fileName: file.name, key: presign.key, sizeBytes: file.size,
      };
    } catch (reason) {
      setAttachmentUploadError(reason instanceof Error ? reason.message : "Could not upload the file.");
      return null;
    } finally {
      setAttachmentUploadBusy(false);
    }
  };
  const addComment = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const hasText = !!commentBody && !!docToPlainText(commentBody).trim();
    if (!hasText && commentAttachments.length === 0) return;
    try {
      const response = await api<{ task: Task | null }>(`/api/tasks/${task.id}/comments`, {
        body: JSON.stringify({ attachments: commentAttachments, body: commentBody }),
        method: "POST",
      });
      setCommentBody(null);
      setCommentAttachments([]);
      if (response.task) onUpdated(response.task);
      await loadComments();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Could not add comment."); }
  };
  // The Claude-style question card only exists on tasks assigned to the Coding
  // Assistant; every other task keeps the plain comment UI untouched. The open
  // question set is the latest agent comment carrying one, and only while no
  // later comment (the human's answer) has followed it.
  const isAgentTask = task.assigneeId === CODING_ASSISTANT_USER_ID;
  const openQuestionComment = useMemo(() => {
    if (!isAgentTask) return null;
    const last = comments[0]; // the API returns newest first
    return last && isAgentComment(last.body) && extractAgentQuestions(last.body) ? last : null;
  }, [comments, isAgentTask]);
  const submitAgentAnswers = async (answers: AgentAnswer[]) => {
    const questions = openQuestionComment ? extractAgentQuestions(openQuestionComment.body) : null;
    if (!questions) return;
    setAnswerBusy(true);
    try {
      const response = await api<{ task: Task | null }>(`/api/tasks/${task.id}/comments`, {
        body: JSON.stringify({ attachments: [], body: buildAnswersDoc(questions, answers) }),
        method: "POST",
      });
      if (response.task) onUpdated(response.task);
      await loadComments();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Could not send answers."); }
    finally { setAnswerBusy(false); }
  };
  const editComment = async (commentId: string) => {
    const hasText = !!editingCommentBody && !!docToPlainText(editingCommentBody).trim();
    const hasAttachment = editingCommentAttachments.length > 0
      || (comments.find((comment) => comment.id === commentId)?.attachments.length ?? 0) > 0;
    if (!hasText && !hasAttachment) return;
    try {
      const response = await api<{ task: Task | null }>(`/api/tasks/${task.id}/comments/${commentId}`, {
        body: JSON.stringify({ attachments: editingCommentAttachments, body: editingCommentBody }),
        method: "PATCH",
      });
      setEditingCommentId(null);
      setEditingCommentBody(null);
      setEditingCommentAttachments([]);
      if (response.task) onUpdated(response.task);
      await loadComments();
    } catch (reason) { setError(reason instanceof Error ? reason.message : "Could not edit comment."); }
  };
  const deleteExistingAttachment = async (commentId: string, attachmentId: string) => {
    setDeletingAttachmentId(attachmentId);
    try {
      const response = await api<{ task: Task | null }>(`/api/tasks/${task.id}/comments/${commentId}/attachments/${attachmentId}`, { method: "DELETE" });
      if (response.task) onUpdated(response.task);
      await loadComments();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not delete the attachment.");
    } finally {
      setDeletingAttachmentId(null);
    }
  };
  const remove = async () => {
    const ok = await confirm({ title: `Delete ${task.taskKey}? This cannot be undone.`, tone: "danger" });
    if (!ok) return;
    setBusy(true);
    try { await api(`/api/tasks/${task.id}`, { method: "DELETE" }); await onDeleted(); onClose(); }
    catch (reason) { setBusy(false); setError(reason instanceof Error ? reason.message : "Could not delete task."); }
  };
  const addPullRequest = async () => {
    const url = pullRequestUrl.trim();
    if (!url) return;
    setPullRequestBusy(true); setError("");
    try {
      const response = await api<{ task: Task | null }>(`/api/tasks/${task.id}/pull-requests`, { body: JSON.stringify({ url }), method: "POST" });
      setPullRequestUrl("");
      if (response.task) onUpdated(response.task);
      await loadHistory();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not link pull request.");
    } finally {
      setPullRequestBusy(false);
    }
  };
  const unlinkPullRequest = async (pullRequestId: string) => {
    setUnlinkingPullRequestId(pullRequestId); setError("");
    try {
      const response = await api<{ task: Task | null }>(`/api/tasks/${task.id}/pull-requests/${encodeURIComponent(pullRequestId)}`, { method: "DELETE" });
      if (response.task) onUpdated(response.task);
      await loadHistory();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not detach the pull request.");
    } finally {
      setUnlinkingPullRequestId(null);
    }
  };
  const refreshPullRequest = async (pullRequestId: string) => {
    setRefreshingPullRequestId(pullRequestId); setError("");
    try {
      const response = await api<{ task: Task | null }>(`/api/tasks/${task.id}/pull-requests/${encodeURIComponent(pullRequestId)}/refresh`, { method: "POST" });
      if (response.task) onUpdated(response.task);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not refresh the pull request.");
    } finally {
      setRefreshingPullRequestId(null);
    }
  };
  const changeType = async (type: string) => {
    setTypePickerOpen(false);
    if (type === task.type) return;
    setError("");
    try {
      const response = await api<{ task: Task | null }>(`/api/tasks/${task.id}`, { body: JSON.stringify({ type }), method: "PATCH" });
      if (response.task) onUpdated(response.task);
      await loadHistory();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not change task type.");
    }
  };
  // Preview-first: this only asks the model for a suggestion and shows it in
  // AiRefineDialog below. Nothing is written to the task until the user
  // explicitly accepts (acceptAiRefine) -- closing the dialog or clicking
  // Reject leaves title/description completely untouched.
  const openAiRefine = async () => {
    setAiRefine({ busy: true, current: { description, title }, error: "", suggested: null });
    try {
      const result = await api<{
        current: { description: TiptapDoc | null; title: string };
        suggested: { description: TiptapDoc | null; title: string };
      }>(`/api/tasks/${task.id}/ai-refine`, { method: "POST" });
      setAiRefine({ busy: false, current: result.current, error: "", suggested: result.suggested });
    } catch (reason) {
      setAiRefine({
        busy: false,
        current: { description, title },
        error: reason instanceof Error ? reason.message : "Could not generate a suggestion.",
        suggested: null,
      });
    }
  };
  const acceptAiRefine = async () => {
    if (!aiRefine?.suggested) return;
    setAiRefine((current) => (current ? { ...current, busy: true } : current));
    try {
      const result = await api<{ description: TiptapDoc | null; task: Task | null; title: string }>(
        `/api/tasks/${task.id}/ai-refine/accept`,
        { body: JSON.stringify(aiRefine.suggested), method: "POST" },
      );
      setTitle(result.title);
      setDescription(result.description);
      setAiRefine(null);
      if (result.task) onUpdated(result.task);
      await loadHistory();
    } catch (reason) {
      setAiRefine((current) => (current
        ? { ...current, busy: false, error: reason instanceof Error ? reason.message : "Could not apply the suggestion." }
        : current));
    }
  };
  return (
    <>
    <div className="dialog-backdrop task-drawer-backdrop" onMouseDown={onClose} role="presentation">
      <section className="dialog task-detail-dialog task-drawer" onMouseDown={(event) => event.stopPropagation()} ref={dialogRef} role="dialog" aria-modal="true">
        <header className="task-drawer-header">
          <span className="task-drawer-key">
            <span className="type-picker" ref={typePickerRef}>
              <button
                aria-expanded={typePickerOpen}
                aria-label={`Change task type (currently ${issueTypeMeta(task.type).label})`}
                className="type-picker-trigger"
                onClick={() => setTypePickerOpen((value) => !value)}
                type="button"
              >
                <IssueTypeIcon type={task.type} />
              </button>
              {typePickerOpen && (
                <div className="type-picker-menu" role="menu">
                  {ISSUE_TYPES.map((type) => (
                    <button aria-current={type === task.type} key={type} onClick={() => void changeType(type)} role="menuitem" type="button">
                      <IssueTypeIcon type={type} /> {issueTypeMeta(type).label}
                    </button>
                  ))}
                </div>
              )}
            </span>
            {" "}{task.taskKey}
          </span>
          <div>
            <button
              aria-label={linkCopied ? "Task link copied" : "Copy task link"}
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(taskUrl);
                  setLinkCopied(true);
                  showToast(`Link to ${task.taskKey} copied to clipboard.`);
                  window.setTimeout(() => setLinkCopied(false), 1500);
                } catch { setError("Could not copy the link — copy it from the address bar instead."); }
              }}
              title={linkCopied ? "Link copied" : "Copy task link"}
              type="button"
            >
              <i aria-hidden="true" className={`bi ${linkCopied ? "bi-check2" : "bi-link-45deg"}`} />
            </button>
            <button className="dialog-close" onClick={onClose} type="button">×</button>
          </div>
        </header>
        <h2 className="sr-only">Task details</h2>
        <div aria-label="Task detail sections" className="source-tabs" role="tablist">
          <button aria-selected={activeTab === "details"} onClick={() => setActiveTab("details")} role="tab" type="button">Details</button>
          <button aria-selected={activeTab === "dependencies"} onClick={() => setActiveTab("dependencies")} role="tab" type="button">Dependencies</button>
          <button aria-selected={activeTab === "history"} onClick={() => setActiveTab("history")} role="tab" type="button">History</button>
        </div>
        <ErrorBanner>{error}</ErrorBanner>
        {activeTab === "details" && (
        <>
        <form className="task-detail-form" onSubmit={save}>
          {/* The icon button is a sibling of the label, not nested inside it --
              label is `display: grid` globally, so a sibling nested inside it
              would become an extra grid row (see docs/steering/structure.md's
              CSS gotchas note). */}
          <div className="task-detail-title-row">
            <label className="task-detail-title">
              <span className="sr-only">Title</span>
              <input aria-describedby={titleTooLong ? "task-detail-title-error" : undefined} aria-invalid={titleTooLong || undefined} aria-label="Title" name="title" onChange={(event) => setTitle(event.target.value)} required value={title} />
            </label>
            <button
              aria-label="Refine with AI"
              className="task-refine-button"
              disabled={aiRefine?.busy}
              onClick={() => void openAiRefine()}
              title="Refine with AI"
              type="button"
            >
              <i aria-hidden="true" className={`bi ${aiRefine?.busy ? "bi-arrow-repeat spin" : "bi-stars"}`} />
            </button>
          </div>
          {titleTooLong && <span className="field-error" id="task-detail-title-error" role="alert">Title must be {MAX_TITLE_LENGTH} characters or fewer.</span>}
          <div className="task-detail-layout">
            <div className="task-detail-main">
              <div className="task-description-field">
                <label>Description
                  {editingDescription ? (
                    <RichTextEditor
                      ariaLabel="Task description"
                      characterLimit={10000}
                      members={members}
                      onChange={setDescription}
                      placeholder="Add a description — type @ to mention someone"
                      taskId={task.id}
                      value={description}
                    />
                  ) : (
                    <RichTextView className="rich-text-preview" doc={description} />
                  )}
                </label>
                {/* Deliberately a sibling of <label>, not nested inside it -- a <button>
                    is a labelable element, so nesting it inside the <label> would make
                    its accessible name the label's entire text content (e.g. "Description
                    Formatted description with a link.") instead of just "Edit"/"Preview". */}
                <button className="text-button" onClick={() => setEditingDescription(!editingDescription)} type="button">
                  {editingDescription ? "Preview" : "Edit"}
                </button>
              </div>
              <div className="task-detail-linked-prs">
                <h3>Linked pull requests</h3>
                {task.pullRequests.length > 0 && (
                  <div className="task-card-pull-requests" aria-label="Linked pull requests">
                    {task.pullRequests.map((pullRequest) => {
                      const activity = relativeTimeFrom(pullRequest.lastActivityAt);
                      const refreshing = refreshingPullRequestId === pullRequest.pullRequestId;
                      const unlinking = unlinkingPullRequestId === pullRequest.pullRequestId;
                      return (
                        <span
                          className={`pull-request-badge pull-request-badge-${pullRequestBadgeVariant(pullRequest)}`}
                          key={`${pullRequest.repositoryName}-${pullRequest.pullRequestId}`}
                        >
                          <a href={pullRequest.url} rel="noreferrer" target="_blank" title={pullRequest.title}>
                            PR #{pullRequest.pullRequestId} · {pullRequestStatusLabel(pullRequest)}{activity ? ` · ${activity}` : ""}
                          </a>
                          {pullRequest.approvalCount !== null && (
                            <span
                              className="pull-request-approvals"
                              title={`${pullRequest.approvalCount} approval${pullRequest.approvalCount === 1 ? "" : "s"}`}
                            >
                              <i aria-hidden="true" className="bi bi-check2-circle" />{pullRequest.approvalCount}
                            </span>
                          )}
                          {pullRequest.errorMessage && (
                            <span className="pull-request-sync-warning" title={`Could not refresh from AWS CodeCommit: ${pullRequest.errorMessage}`}>
                              <i aria-hidden="true" className="bi bi-exclamation-triangle-fill" />
                            </span>
                          )}
                          <button
                            aria-label={`Refresh status for PR #${pullRequest.pullRequestId}`}
                            className="pull-request-refresh"
                            disabled={refreshing}
                            onClick={() => void refreshPullRequest(pullRequest.pullRequestId)}
                            title="Refresh pull request status"
                            type="button"
                          >
                            <i aria-hidden="true" className={`bi bi-arrow-repeat${refreshing ? " spin" : ""}`} />
                          </button>
                          <button
                            aria-label={`Detach PR #${pullRequest.pullRequestId} from this task`}
                            className="pull-request-unlink"
                            disabled={unlinking}
                            onClick={() => void unlinkPullRequest(pullRequest.pullRequestId)}
                            title="Detach this pull request from the task"
                            type="button"
                          >
                            <i aria-hidden="true" className="bi bi-x-lg" />
                          </button>
                        </span>
                      );
                    })}
                  </div>
                )}
                <div className="task-detail-pr-add">
                  <label>
                    <span className="sr-only">CodeCommit pull request URL</span>
                    <input
                      aria-label="CodeCommit pull request URL"
                      onChange={(event) => setPullRequestUrl(event.currentTarget.value)}
                      placeholder="https://<region>.console.aws.amazon.com/codesuite/codecommit/repositories/<repo>/pull-requests/<id>/details"
                      type="url"
                      value={pullRequestUrl}
                    />
                  </label>
                  <button
                    className="button secondary"
                    disabled={!pullRequestUrl.trim() || pullRequestBusy}
                    onClick={() => void addPullRequest()}
                    type="button"
                  >
                    Link PR
                  </button>
                </div>
              </div>
            </div>
            <aside className="task-detail-sidebar">
              <h3>Details</h3>
              <div className="task-form-grid">
            <label>Assignee<FieldPicker
              ariaLabel={`Change assignee (currently ${memberById(members, assigneeId)?.displayName ?? "Unassigned"})`}
              items={members}
              name="assigneeId"
              onSelect={setAssigneeId}
              renderLabel={(member) => <><span className="member-avatar"><AvatarContent allNames={memberNames} avatarUrl={member.avatarUrl} name={member.displayName} /></span>{member.displayName}</>}
              renderTrigger={<><span className="member-avatar"><AvatarContent allNames={memberNames} avatarUrl={memberById(members, assigneeId)?.avatarUrl} name={memberById(members, assigneeId)?.displayName ?? "Unassigned"} /></span>{memberById(members, assigneeId)?.displayName ?? "Unassigned"}</>}
              selectedId={assigneeId}
            /></label>
            <label>Reporter<span className="static-field reporter-field">{task.reporterId
              ? <><span className="member-avatar"><AvatarContent allNames={memberNames} avatarUrl={memberById(members, task.reporterId)?.avatarUrl} name={task.reporterName ?? ""} /></span>{task.reporterName}</>
              : "Unassigned"}</span></label>
            <label>Priority<FieldPicker
              ariaLabel={`Change priority (currently ${priorityMeta(priority).label})`}
              items={PRIORITY_OPTIONS.map((option) => ({ id: option.value, label: option.label }))}
              name="priority"
              onSelect={setPriority}
              renderLabel={(option) => <><PriorityIcon priority={option.id} />{option.label}</>}
              renderTrigger={<><PriorityIcon priority={priority} />{priorityMeta(priority).label}</>}
              selectedId={priority}
            /></label>
            <label>Status<FieldPicker
              ariaLabel={`Change status (currently ${columns.find(({ id }) => id === columnId)?.name ?? ""})`}
              items={columns}
              name="columnId"
              onSelect={setColumnId}
              renderLabel={(column) => column.name}
              renderTrigger={columns.find(({ id }) => id === columnId)?.name ?? ""}
              selectedId={columnId}
            /></label>
            <label>Sprint<FieldPicker
              ariaLabel={`Change sprint (currently ${sprints.find(({ id }) => id === sprintId)?.name ?? "Backlog"})`}
              items={[{ id: "", name: "Backlog" }, ...sprints.filter(({ state }) => ["planned", "active"].includes(state))]}
              name="sprintId"
              onSelect={setSprintId}
              renderLabel={(sprint) => sprint.name}
              renderTrigger={sprints.find(({ id }) => id === sprintId)?.name ?? "Backlog"}
              selectedId={sprintId}
            /></label>
            <label>Estimate<input defaultValue={task.estimatePoints ?? ""} min="0" name="estimatePoints" step="0.25" type="number" /></label>
            <label>Due date<input defaultValue={task.dueOn ?? ""} name="dueOn" type="date" /></label>
            {(task.type === "epic" || task.type === "story") && (
              <>
                <label>
                  <span>Planned start</span>
                  <input defaultValue={task.startsOn ?? ""} name="startsOn" type="date" />
                </label>
                <label>
                  <span>Planned target</span>
                  <input defaultValue={task.targetOn ?? ""} name="targetOn" type="date" />
                </label>
                <label>Linked goal<FieldPicker
                  ariaLabel={`Change linked goal (currently ${goals.find(({ id }) => id === goalId)?.name ?? "none"})`}
                  items={[{ id: "", name: "No linked goal" }, ...goals]}
                  name="goalId"
                  onSelect={setGoalId}
                  renderLabel={(option) => option.name}
                  renderTrigger={goals.find(({ id }) => id === goalId)?.name ?? "No linked goal"}
                  selectedId={goalId}
                /></label>
              </>
            )}
            <label>Labels<LabelsField defaultLabels={task.labels} name="labels" /></label>
            <label>Parent task<FieldPicker
              ariaLabel={`Change parent task (currently ${tasks.find(({ id }) => id === parentId)?.taskKey ?? "no parent"})`}
              getSearchText={(option) => option.label}
              items={parentTaskOptions(tasks, task.id)}
              name="parentId"
              onSelect={setParentId}
              renderLabel={(option) => option.label}
              renderTrigger={parentId ? `${tasks.find(({ id }) => id === parentId)?.taskKey ?? ""} · ${tasks.find(({ id }) => id === parentId)?.title ?? ""}` : "No parent"}
              searchAriaLabel="Search parent tasks"
              searchable
              selectedId={parentId}
            /></label>
            <label>Reviewers<ReviewerPickerField
              ariaLabel={`Edit reviewers for ${task.taskKey} (${task.reviewers.length} selected)`}
              busy={reviewersBusy}
              members={members}
              onChange={(reviewerIds) => {
                setReviewersBusy(true);
                api<{ task: Task | null }>(`/api/tasks/${task.id}/reviewers`, { body: JSON.stringify({ reviewerIds }), method: "PUT" })
                  .then((response) => {
                    if (response.task) onUpdated(response.task);
                    return loadHistory();
                  })
                  .catch((reason) => setError(reason instanceof Error ? reason.message : "Could not update reviewers."))
                  .finally(() => setReviewersBusy(false));
              }}
              reviewerIds={new Set(task.reviewers.map((reviewer) => reviewer.reviewerId))}
              reviewers={task.reviewers}
            /></label>
            <label>
              Related link
              <input defaultValue={task.externalLinkUrl ?? ""} name="externalLinkUrl" placeholder="https://…" type="url" />
            </label>
            {task.externalLinkUrl && (
              <a className="task-detail-external-link" href={task.externalLinkUrl} rel="noreferrer" target="_blank">
                Open related link ↗
              </a>
            )}
              </div>
            </aside>
          </div>
          <div className="dialog-actions split-actions">
            {canDelete && <button className="button danger" disabled={busy} onClick={() => void remove()} type="button">Delete task</button>}
            <span />
            <button className="button secondary" onClick={onClose} type="button">Cancel</button>
            <button className="button primary" disabled={busy || titleTooLong} type="submit">{busy ? "Saving…" : "Save changes"}</button>
          </div>
        </form>
        <section className="task-comments task-drawer-section">
          <h3>Comments</h3>
          <div>{comments.map((comment) => (
            <article className={isAgentAuthored(comment) ? "comment-agent" : undefined} data-author-kind={isAgentAuthored(comment) ? "agent" : "human"} key={comment.id}>
              <strong>{comment.authorName}{isAgentAuthored(comment) && <span className="comment-badge">Agent</span>}</strong>
              <time>{formatServerDateTime(comment.createdAt)}{comment.updatedAt !== comment.createdAt ? " · edited" : ""}</time>
              {editingCommentId === comment.id ? (
                <div className="comment-editor">
                  <RichTextEditor ariaLabel="Edit task comment" characterLimit={5000} members={members} onChange={setEditingCommentBody} placeholder="Type @ to mention someone" taskId={task.id} value={editingCommentBody} />
                  <PendingAttachmentList
                    attachments={editingCommentAttachments}
                    onRemove={(attachmentId) => setEditingCommentAttachments((current) => current.filter((attachment) => attachment.attachmentId !== attachmentId))}
                  />
                  <div className="comment-compose-actions">
                    <AttachFileButton
                      busy={attachmentUploadBusy}
                      onSelect={(file) => void uploadFileAttachment(file).then((uploaded) => { if (uploaded) setEditingCommentAttachments((current) => [...current, uploaded]); })}
                    />
                    <button onClick={() => { setEditingCommentId(null); setEditingCommentBody(null); setEditingCommentAttachments([]); }} type="button">Cancel</button>
                    <button onClick={() => void editComment(comment.id)} type="button">Save</button>
                  </div>
                </div>
              ) : isAgentTask && extractAgentQuestions(comment.body) ? (
                openQuestionComment?.id === comment.id ? (
                  <AgentQuestionsCard
                    busy={answerBusy}
                    intro={docToPlainText(stripAgentMarker(comment.body)).split("\n")[0] ?? ""}
                    onSubmit={(answers) => void submitAgentAnswers(answers)}
                    questions={extractAgentQuestions(comment.body) ?? []}
                  />
                ) : <RichTextView doc={withoutQuestionsBlock(stripAgentMarker(comment.body))} />
              ) : <RichTextView doc={comment.body} />}
              {comment.attachments.length > 0 && (
                <ExistingAttachmentList
                  attachments={comment.attachments}
                  canDelete={canDelete || comment.authorId === currentUserId}
                  commentId={comment.id}
                  deletingAttachmentId={deletingAttachmentId}
                  onDelete={(attachmentId) => void deleteExistingAttachment(comment.id, attachmentId)}
                  taskId={task.id}
                />
              )}
              <div className="comment-actions">
                {comment.authorId === currentUserId && <button onClick={() => { setEditingCommentId(comment.id); setEditingCommentBody(comment.body); setEditingCommentAttachments([]); }} type="button">Edit</button>}
                {(canDelete || comment.authorId === currentUserId) && <button onClick={async () => { const response = await api<{ task: Task | null }>(`/api/tasks/${task.id}/comments/${comment.id}`, { method: "DELETE" }); if (response.task) onUpdated(response.task); await loadComments(); }} type="button">Delete</button>}
              </div>
            </article>
          ))}</div>
          <form onSubmit={addComment}>
            <RichTextEditor ariaLabel="New task comment" characterLimit={5000} members={members} onChange={setCommentBody} placeholder="Add context or an update — type @ to mention someone" taskId={task.id} value={commentBody} />
            <PendingAttachmentList
              attachments={commentAttachments}
              onRemove={(attachmentId) => setCommentAttachments((current) => current.filter((attachment) => attachment.attachmentId !== attachmentId))}
            />
            {attachmentUploadError && <ErrorBanner>{attachmentUploadError}</ErrorBanner>}
            <div className="comment-compose-actions">
              <AttachFileButton
                busy={attachmentUploadBusy}
                onSelect={(file) => void uploadFileAttachment(file).then((uploaded) => { if (uploaded) setCommentAttachments((current) => [...current, uploaded]); })}
              />
              <button className="button secondary" type="submit">Add comment</button>
            </div>
          </form>
        </section>
        </>
        )}
        {activeTab === "dependencies" && (
        <section className="task-dependencies task-drawer-section">
          <h3>Dependencies</h3>
          <div className="dependency-columns">
            <div><strong>Blocked by</strong>{dependencies.blockers.length ? dependencies.blockers.map((blocker) => <article key={blocker.id}><span>{blocker.taskKey} · {blocker.title}</span><button onClick={async () => { await api(`/api/tasks/${task.id}/dependencies/${blocker.id}`, { method: "DELETE" }); await Promise.all([loadDependencies(), loadHistory()]); }} type="button">Remove</button></article>) : <p>Nothing blocks this task.</p>}</div>
            <div><strong>Blocks</strong>{dependencies.blocking.length ? dependencies.blocking.map((blocked) => <article key={blocked.id}><span>{blocked.taskKey} · {blocked.title}</span></article>) : <p>This task blocks nothing.</p>}</div>
          </div>
          <form onSubmit={async (event) => { event.preventDefault(); const form = event.currentTarget; const blockerId = String(new FormData(form).get("blockerId") ?? ""); if (!blockerId) return; try { await api(`/api/tasks/${task.id}/dependencies`, { body: JSON.stringify({ blockerId }), method: "POST" }); form.reset(); await Promise.all([loadDependencies(), loadHistory()]); } catch (reason) { setError(reason instanceof Error ? reason.message : "Could not add dependency."); } }}>
            <label><span>Add blocker<RequiredMark /></span><select aria-label="Add blocking task" defaultValue="" name="blockerId" required><option value="" disabled>Choose task</option>{tasks.filter(({ id }) => id !== task.id && !dependencies.blockers.some((blocker) => blocker.id === id)).map((candidate) => <option key={candidate.id} value={candidate.id}>{candidate.taskKey} · {candidate.title}</option>)}</select></label>
            <button className="button secondary" type="submit">Add dependency</button>
          </form>
        </section>
        )}
        {activeTab === "history" && (
        <section className="task-history task-drawer-section">
          <h3>History</h3>
          {history.length ? history.map((entry) => (
            <article key={entry.id}>
              <header><strong>{entry.actorName}</strong> <time>{relativeTimeFrom(entry.createdAt) ?? formatServerDateTime(entry.createdAt)}</time></header>
              <ul>
                {entry.eventType === "created" && <li>created this work item</li>}
                {entry.changes.map((change, index) => (
                  <li key={index}>
                    {change.before == null
                      ? <>set <strong>{change.label}</strong> to <strong>{change.after}</strong></>
                      : change.after == null
                        ? <>removed <strong>{change.label}</strong> (was <strong>{change.before}</strong>)</>
                        : <>changed <strong>{change.label}</strong> from <strong>{change.before}</strong> to <strong>{change.after}</strong></>}
                  </li>
                ))}
              </ul>
            </article>
          )) : <p>No history yet.</p>}
        </section>
        )}
      </section>
    </div>
    {pendingReviewEntry?.step === "link" && (
      <ReviewEntryDialog
        busy={pendingReviewEntry.busy}
        columnName={columns.find(({ id }) => id === pendingReviewEntry.targetColumnId)?.name ?? ""}
        displayName={memberById(members, currentUserId)?.displayName ?? "you"}
        error={pendingReviewEntry.error}
        onCancel={() => setPendingReviewEntry(null)}
        onSubmit={(link) => void submitPendingReviewEntry(link)}
      />
    )}
    {pendingReviewEntry?.step === "reviewers" && (
      <ReviewerSelectDialog
        busy={pendingReviewEntry.busy}
        error={pendingReviewEntry.error}
        members={members}
        onDone={() => void finishPendingReviewEntry()}
        onToggle={(reviewerIds) => void setPendingReviewEntryReviewers(reviewerIds)}
        selectedIds={new Set(task.reviewers.map((reviewer) => reviewer.reviewerId))}
      />
    )}
    {aiRefine && (
      <AiRefineDialog
        busy={aiRefine.busy}
        current={aiRefine.current}
        error={aiRefine.error}
        onAccept={() => void acceptAiRefine()}
        onClose={() => setAiRefine(null)}
        onRetry={() => void openAiRefine()}
        suggested={aiRefine.suggested}
      />
    )}
    </>
  );
}

// Preview-first "Refine with AI" flow -- see openAiRefine/acceptAiRefine
// above. Nothing is ever written to the task from here; the dialog only
// shows a proposed title/description next to the current ones. Accept
// applies exactly the suggestion shown (see acceptTaskRefinement in
// tasks-api.ts), Reject/close just discards it.
function AiRefineDialog({
  busy,
  current,
  error,
  onAccept,
  onClose,
  onRetry,
  suggested,
}: {
  busy: boolean;
  current: { description: TiptapDoc | null; title: string };
  error: string;
  onAccept: () => void;
  onClose: () => void;
  onRetry: () => void;
  suggested: { description: TiptapDoc | null; title: string } | null;
}) {
  const dialogRef = useModalA11y<HTMLElement>(onClose);
  const unchanged = suggested
    && suggested.title === current.title
    && JSON.stringify(suggested.description) === JSON.stringify(current.description);
  return (
    <div className="dialog-backdrop" onMouseDown={onClose} role="presentation">
      <section aria-labelledby="ai-refine-dialog-title" className="dialog ai-refine-dialog" onMouseDown={(event) => event.stopPropagation()} ref={dialogRef} role="dialog" aria-modal="true">
        <button className="dialog-close" disabled={busy} onClick={onClose} type="button">×</button>
        <p className="eyebrow">Refine with AI</p>
        <h2 id="ai-refine-dialog-title">Review the suggested title and description</h2>
        <ErrorBanner>{error}</ErrorBanner>
        {busy && !suggested && !error && <p>Asking the configured model for a suggestion…</p>}
        {suggested && (
          unchanged ? (
            <p>The model didn't propose any change — the current title and description already read clearly.</p>
          ) : (
            <div className="ai-refine-diff">
              <div>
                <p className="eyebrow">Current</p>
                <p className="ai-refine-title">{current.title}</p>
                {docToPlainText(current.description).trim() ? <RichTextView className="ai-refine-description" doc={current.description} /> : <p className="ai-refine-description"><em>No description</em></p>}
              </div>
              <div>
                <p className="eyebrow">Suggested</p>
                <p className="ai-refine-title">{suggested.title}</p>
                {docToPlainText(suggested.description).trim() ? <RichTextView className="ai-refine-description" doc={suggested.description} /> : <p className="ai-refine-description"><em>No description</em></p>}
              </div>
            </div>
          )
        )}
        <div className="dialog-actions">
          {error ? (
            <button className="button secondary" disabled={busy} onClick={onRetry} type="button">Try again</button>
          ) : (
            <button className="button secondary" disabled={busy} onClick={onClose} type="button">
              {suggested ? "Reject" : "Cancel"}
            </button>
          )}
          {suggested && !unchanged && (
            <button className="button primary" disabled={busy} onClick={onAccept} type="button">
              {busy ? "Applying…" : "Accept"}
            </button>
          )}
        </div>
      </section>
    </div>
  );
}

export function TaskWorkspace({
  currentUserId,
  initialQuery,
  isAdmin,
  members,
  mode,
  onNavigate,
  onOpenSprints,
  onQueryChange,
  sprintId,
  taskId,
  teamId,
}: {
  currentUserId: string;
  initialQuery?: string;
  isAdmin: boolean;
  members: Member[];
  mode: TaskRouteMode;
  onNavigate: (route: { mode: TaskRouteMode; query?: string; sprintId?: string | null; taskId?: string | null }) => void;
  onOpenSprints?: () => void;
  onQueryChange?: (query: string) => void;
  sprintId: string | null;
  taskId: string | null;
  teamId: string;
}) {
  const confirm = useConfirm();
  const showToast = useToast();
  const [data, setData] = useState<TaskData | null>(null);
  const [error, setError] = useState("");
  const [creationNotice, setCreationNotice] = useState("");
  const [query, setQuery] = useState(initialQuery ?? "");
  const [type, setType] = useState("");
  const [assignee, setAssignee] = useState("");
  const [assigneeOverflowOpen, setAssigneeOverflowOpen] = useState(false);
  const [groupBy, setGroupBy] = useState<"none" | "assignee">("assignee");
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const [collapsedBacklogGroups, setCollapsedBacklogGroups] = useState<Set<string>>(new Set());
  const [createOpen, setCreateOpen] = useState(false);
  const [draggedTaskId, setDraggedTaskId] = useState<string | null>(null);
  const [dragTargetColumnId, setDragTargetColumnId] = useState<string | null>(null);
  // Which card is currently being dragged over, for the "insert above this card"
  // reorder gesture (see the per-card drag handlers in the column render below).
  const [reorderTargetTaskId, setReorderTargetTaskId] = useState<string | null>(null);
  const [boardSettingsOpen, setBoardSettingsOpen] = useState(false);
  const [selectedBacklogTasks, setSelectedBacklogTasks] = useState<Set<string>>(new Set());
  const [bulkDeleteBusy, setBulkDeleteBusy] = useState(false);
  const [bulkMoveBusy, setBulkMoveBusy] = useState(false);
  const [sprintCreateOpen, setSprintCreateOpen] = useState(false);
  const [sprintCreateBusy, setSprintCreateBusy] = useState(false);
  const [scrumMemberIndex, setScrumMemberIndex] = useState(0);
  const memberNames = members.map((member) => member.displayName);
  // step "link" is the B.1 prompt (PR/document link, or an explicit no-link
  // confirmation); step "reviewers" is B.2's follow-on prompt, shown after
  // the link step resolves but before the move itself commits — matching
  // the plan's "same move-into-review action, after the link step" order.
  const [reviewEntry, setReviewEntry] = useState<{ busy: boolean; column: Column; error: string; position?: number; step: "link" | "reviewers"; task: Task } | null>(null);

  const load = useCallback(async () => {
    const params = new URLSearchParams({ teamId });
    if (query) params.set("query", query);
    if (type) params.set("type", type);
    if (assignee) params.set("assigneeId", assignee);
    const result = await api<TaskData>(`/api/tasks?${params}`);
    setData(result);
  }, [assignee, query, teamId, type]);
  // Replaces one task's row in local state immediately (no round trip to
  // re-fetch the whole team's board), so a save/move/comment/etc. feels
  // instant instead of blocking on a full GET /api/tasks -- see the plan doc
  // for why that reload was the dominant source of perceived save latency.
  // Still fires a background, unawaited resync afterward to pick up anything
  // the single-task response can't reflect (e.g. another user's concurrent
  // change), without reintroducing the latency this exists to remove.
  const patchTask = useCallback((updated: Task) => {
    setData((current) => (current
      ? { ...current, tasks: current.tasks.map((existing) => (existing.id === updated.id ? updated : existing)) }
      : current));
    void load().catch(() => {});
  }, [load]);

  useEffect(() => {
    const timeout = window.setTimeout(() => {
      load().catch((reason) => setError(reason instanceof Error ? reason.message : "Could not load tasks."));
    }, query ? 200 : 0);
    return () => window.clearTimeout(timeout);
  }, [load, query]);

  useEffect(() => setScrumMemberIndex(0), [teamId, sprintId]);
  useEffect(() => setQuery(initialQuery ?? ""), [initialQuery]);

  useEffect(() => {
    if (!data || !sprintId) return;
    if (!data.sprints.some((sprint) => sprint.id === sprintId)) {
      onNavigate({ mode, sprintId: null, taskId });
    }
  }, [data, mode, onNavigate, sprintId, taskId]);

  // The board always shows exactly one sprint's work, not a mix of every sprint plus
  // the backlog. As soon as data loads, jump straight to whichever sprint is active —
  // teams with no sprints at all (pure Kanban) are left alone, since there's nothing
  // to scope to and they should keep seeing their backlog on the board. Passes
  // `taskId` through unchanged (e.g. a /t/:taskKey link landing here with no sprint in
  // the URL yet) -- omitting it reads as "navigate to no task" and would silently close
  // whatever task this defaulting was supposed to happen underneath.
  useEffect(() => {
    if (mode !== "board" || sprintId || !data?.sprints.length) return;
    const active = data.sprints.find((sprint) => sprint.state === "active");
    if (active) onNavigate({ mode, sprintId: active.id, taskId });
  }, [data, mode, onNavigate, sprintId, taskId]);

  useEffect(() => {
    const create = () => setCreateOpen(true);
    const search = (event: Event) => {
      const value = String((event as CustomEvent<string>).detail ?? "");
      setQuery(value); onQueryChange?.(value);
    };
    window.addEventListener("taskei:create-task", create);
    window.addEventListener("taskei:search-tasks", search);
    return () => {
      window.removeEventListener("taskei:create-task", create);
      window.removeEventListener("taskei:search-tasks", search);
    };
  }, [onQueryChange]);

  // `position` defaults to "append to the bottom" (a fresh, always-largest
  // timestamp) for every existing caller (column-picker, status select,
  // dropping on empty column space). Dragging a card and dropping it
  // directly onto another card instead computes an explicit interpolated
  // position (see the board-column render below) so the task lands exactly
  // where it was dropped instead of always bouncing to the bottom.
  const moveTask = async (task: Task, column: Column, position: number = Date.now()) => {
    setError("");
    try {
      const response = await api<{ task: Task | null }>(`/api/tasks/${task.id}`, {
        body: JSON.stringify({
          columnId: column.id,
          position,
        }),
        method: "PATCH",
      });
      if (response.task) patchTask(response.task);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not move task.");
    }
  };
  // Every user-initiated move (drag-and-drop, the card's column picker, the
  // backlog row's status select) funnels through here instead of calling
  // moveTask directly, so a genuine entry into a review-flagged column — the
  // task's *current* column isn't already review-flagged — pauses for the
  // link/no-link prompt instead of moving immediately. Already-in-review and
  // moving-out-of-review both skip straight to moveTask, same as before. A
  // task that already has a PR link or an external doc link attached also
  // skips the prompt, since the thing it exists to collect is already there.
  const requestMove = async (task: Task, column: Column, position?: number) => {
    const currentColumnIsReview = Boolean(data?.columns.find(({ id }) => id === task.columnId)?.isReview);
    const hasLink = Boolean(task.externalLinkUrl) || task.pullRequests.length > 0;
    if (column.isReview && !currentColumnIsReview && !hasLink) {
      setReviewEntry({ busy: false, column, error: "", position, step: "link", task });
      return;
    }
    await moveTask(task, column, position);
  };
  const submitReviewEntry = async (link: string | null) => {
    if (!reviewEntry) return;
    setReviewEntry((current) => (current ? { ...current, busy: true, error: "" } : current));
    try {
      await submitReviewEntryLink(reviewEntry.task.id, link, memberById(members, currentUserId)?.displayName ?? "a teammate");
      setReviewEntry((current) => (current ? { ...current, busy: false, step: "reviewers" } : current));
    } catch (reason) {
      setReviewEntry((current) => (current
        ? { ...current, busy: false, error: reason instanceof Error ? reason.message : "Could not complete the move." }
        : current));
    }
  };
  // The reviewer step's own onChange fires this per checkbox toggle (immediate
  // PATCH, same convention as everywhere else reviewers are editable) rather
  // than batching the whole selection until some final "Done" click — there
  // is no separate submit action for this step, just "close when satisfied."
  const setReviewEntryReviewers = async (reviewerIds: string[]) => {
    if (!reviewEntry) return;
    setReviewEntry((current) => (current ? { ...current, busy: true, error: "" } : current));
    try {
      const response = await api<{ task: Task | null }>(`/api/tasks/${reviewEntry.task.id}/reviewers`, { body: JSON.stringify({ reviewerIds }), method: "PUT" });
      if (response.task) patchTask(response.task);
      setReviewEntry((current) => (current ? { ...current, busy: false } : current));
    } catch (reason) {
      setReviewEntry((current) => (current
        ? { ...current, busy: false, error: reason instanceof Error ? reason.message : "Could not update reviewers." }
        : current));
    }
  };
  const finishReviewEntry = async () => {
    if (!reviewEntry) return;
    setReviewEntry((current) => (current ? { ...current, busy: true, error: "" } : current));
    try {
      await moveTask(reviewEntry.task, reviewEntry.column, reviewEntry.position);
      setReviewEntry(null);
    } catch (reason) {
      setReviewEntry((current) => (current
        ? { ...current, busy: false, error: reason instanceof Error ? reason.message : "Could not complete the move." }
        : current));
    }
  };
  const refreshCardPullRequest = async (task: Task, pullRequestId: string) => {
    setError("");
    try {
      const response = await api<{ task: Task | null }>(`/api/tasks/${task.id}/pull-requests/${encodeURIComponent(pullRequestId)}/refresh`, { method: "POST" });
      if (response.task) patchTask(response.task);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not refresh the pull request.");
    }
  };

  // Epics are containers for other work items, not actual pieces of work
  // themselves, so they never render as board/Kanban cards -- regardless of
  // which sprint is selected or whether the epic itself carries a sprintId.
  // They still show in the backlog and the hierarchy view.
  const visibleTasks = useMemo(
    () => (data?.tasks ?? []).filter((task) => task.type !== "epic" && (!sprintId || task.sprintId === sprintId)),
    [data?.tasks, sprintId],
  );
  // True once the team has at least one sprint but none is currently selected (no
  // sprint is active, and the user hasn't picked a planned/completed one either) — the
  // board has nothing single-sprint to show, so it prompts instead of falling back to
  // "everything." Teams with zero sprints never hit this: they keep showing backlog.
  const boardNeedsSprint = mode === "board" && !sprintId && Boolean(data?.sprints.length);
  // Once a specific sprint is on screen, the board's assignee filter is
  // scoped to it, so a member with no work in THIS sprint shouldn't get a
  // permanent avatar chip cluttering the filter. The server applies the
  // assignee/search/type filters, so `visibleTasks` is already narrowed by
  // them: deriving the chips from it made every other person vanish the
  // moment one was picked. The scope is therefore captured only from an
  // unfiltered load, and reused while a filter is active.
  const [assigneeScope, setAssigneeScope] = useState<{ ids: Set<string>; sprintId: string } | null>(null);
  useEffect(() => {
    if (!sprintId || assignee || query || type) return;
    setAssigneeScope({ ids: sprintAssigneeIds(visibleTasks), sprintId });
  }, [assignee, query, sprintId, type, visibleTasks]);
  const assigneeFilterMembers = useMemo(
    () => assigneeFilterCandidates(members, {
      mode,
      selectedId: assignee,
      sprintAssigneeIds: assigneeScope && assigneeScope.sprintId === sprintId ? assigneeScope.ids : null,
      sprintId,
    }),
    [assignee, assigneeScope, members, mode, sprintId],
  );
  const boardGroups = useMemo(() => {
    if (groupBy === "none") return [{ id: "all", name: "All work items", tasks: visibleTasks }];
    const assigned = members.map((member) => ({
      id: member.id,
      name: member.displayName,
      tasks: visibleTasks.filter((task) => task.assigneeId === member.id),
    })).filter((group) => group.tasks.length > 0);
    const unassigned = visibleTasks.filter((task) => !members.some((member) => member.id === task.assigneeId));
    return unassigned.length ? [...assigned, { id: "unassigned", name: "Unassigned", tasks: unassigned }] : assigned;
  }, [groupBy, members, visibleTasks]);

  const assignSprint = async (taskId: string, sprintId: string) => {
    setError("");
    try {
      const response = await api<{ task: Task | null }>(`/api/tasks/${taskId}`, {
        body: JSON.stringify({ sprintId: sprintId || null }),
        method: "PATCH",
      });
      if (response.task) patchTask(response.task);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not assign sprint.");
    }
  };
  // Shared by the card and backlog row FieldPickers for Assignee/Priority/
  // Points — an immediate PATCH-on-select, mirroring moveTask/assignSprint
  // above, instead of the task-detail sidebar's deferred form-submit pattern.
  const patchTaskField = async (taskId: string, body: Record<string, unknown>, errorMessage: string) => {
    setError("");
    try {
      const response = await api<{ task: Task | null }>(`/api/tasks/${taskId}`, { body: JSON.stringify(body), method: "PATCH" });
      if (response.task) patchTask(response.task);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : errorMessage);
    }
  };
  const changeAssignee = (task: Task, assigneeId: string) => patchTaskField(task.id, { assigneeId }, "Could not change the assignee.");
  const changePriority = (task: Task, priority: string) => patchTaskField(task.id, { priority }, "Could not change the priority.");
  const changeEstimatePoints = (task: Task, estimatePoints: number | null) => patchTaskField(task.id, { estimatePoints }, "Could not change the estimate.");
  // Reviewers are editable anytime, not just during the move-into-review flow
  // above — this is the board card's own picker (only shown while the card
  // sits in a review-flagged column).
  const changeReviewers = async (task: Task, reviewerIds: string[]) => {
    setError("");
    try {
      const response = await api<{ task: Task | null }>(`/api/tasks/${task.id}/reviewers`, { body: JSON.stringify({ reviewerIds }), method: "PUT" });
      if (response.task) patchTask(response.task);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not update reviewers.");
    }
  };

  const bulkDeleteSelected = async () => {
    if (!selectedBacklogTasks.size) return;
    const ok = await confirm({
      title: `Delete ${selectedBacklogTasks.size} selected work item${selectedBacklogTasks.size === 1 ? "" : "s"}? This cannot be undone.`,
      tone: "danger",
    });
    if (!ok) return;
    const deletedCount = selectedBacklogTasks.size;
    setBulkDeleteBusy(true); setError("");
    try {
      await api("/api/tasks/bulk-delete", {
        body: JSON.stringify({ taskIds: [...selectedBacklogTasks] }),
        method: "POST",
      });
      setSelectedBacklogTasks(new Set());
      await load();
      showToast(`Deleted ${deletedCount} work item${deletedCount === 1 ? "" : "s"}.`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not delete the selected work items.");
    } finally {
      setBulkDeleteBusy(false);
    }
  };

  // Jira-style sprint creation from the Backlog view -- an additional way to
  // create sprints alongside the Sprints tab (which keeps its own, separate
  // create form), reusing the same POST /api/sprints endpoint.
  const createSprintFromBacklog = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    setSprintCreateBusy(true); setError("");
    try {
      await api("/api/sprints", {
        body: JSON.stringify({
          endsOn: form.get("endsOn"), goal: form.get("goal"), name: form.get("name"),
          startsOn: form.get("startsOn"), teamId,
        }),
        method: "POST",
      });
      formElement.reset();
      setSprintCreateOpen(false);
      await load();
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not create the sprint.");
    } finally {
      setSprintCreateBusy(false);
    }
  };

  // Bulk-moves every currently checked backlog task into (or out of, for
  // "Backlog") one sprint at once, mirroring bulkDeleteSelected's shape --
  // parallel per-task PATCHes rather than a dedicated bulk endpoint, since
  // the multi-select in this view isn't expected to span hundreds of tasks.
  const bulkMoveSelectedToSprint = async (sprintId: string) => {
    if (!selectedBacklogTasks.size) return;
    const taskIds = [...selectedBacklogTasks];
    const destinationName = sprintId ? data?.sprints.find(({ id }) => id === sprintId)?.name ?? "the chosen sprint" : "the backlog";
    setBulkMoveBusy(true); setError("");
    try {
      await Promise.all(taskIds.map((taskId) => api(`/api/tasks/${taskId}`, {
        body: JSON.stringify({ sprintId: sprintId || null }),
        method: "PATCH",
      })));
      setSelectedBacklogTasks(new Set());
      await load();
      showToast(`Moved ${taskIds.length} work item${taskIds.length === 1 ? "" : "s"} to ${destinationName}.`);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Could not move the selected work items.");
    } finally {
      setBulkMoveBusy(false);
    }
  };

  const activeSprintIds = new Set(
    (data?.sprints ?? []).filter(({ state }) => state === "active").map(({ id }) => id),
  );
  const reviewColumnIds = new Set((data?.columns ?? []).filter((column) => column.isReview).map(({ id }) => id));
  const isScrumEligibleTask = (task: Task) => {
    const isInSelectedSprint = sprintId
      ? task.sprintId === sprintId && activeSprintIds.has(sprintId)
      : Boolean(task.sprintId && activeSprintIds.has(task.sprintId));
    if (!isInSelectedSprint || ["done", "cancelled"].includes(task.status)) return false;
    const column = data?.columns.find(({ id }) => id === task.columnId);
    if (reviewColumnIds.has(task.columnId)) return true;
    return column?.category === "in_progress" || column?.category === "todo";
  };
  const scrumEligibleTasks = (data?.tasks ?? []).filter(isScrumEligibleTask);
  const scrumEligibleMembers = members.filter((member) =>
    scrumEligibleTasks.some((task) => task.assigneeId === member.id),
  );
  const scrumMember = scrumEligibleMembers[scrumMemberIndex] ?? scrumEligibleMembers[0];
  const scrumMemberTasks = scrumEligibleTasks.filter((task) => task.assigneeId === scrumMember?.id);
  const priorityRank = (priority: string) => {
    const index = PRIORITY_LEVELS.indexOf(priority as (typeof PRIORITY_LEVELS)[number]);
    return index === -1 ? PRIORITY_LEVELS.length : index;
  };
  const byPriority = (a: Task, b: Task) => priorityRank(a.priority) - priorityRank(b.priority);
  const scrumGroups: Array<{ flagStale: boolean; key: string; label: string; tasks: Task[] }> = [
    {
      flagStale: true,
      key: "in_review",
      label: "In review",
      tasks: scrumMemberTasks.filter((task) => reviewColumnIds.has(task.columnId)).sort(byPriority),
    },
    {
      flagStale: true,
      key: "in_progress",
      label: "In progress",
      tasks: scrumMemberTasks
        .filter((task) => {
          const column = data?.columns.find(({ id }) => id === task.columnId);
          return !reviewColumnIds.has(task.columnId) && column?.category === "in_progress";
        })
        .sort(byPriority),
    },
    {
      flagStale: false,
      key: "todo",
      label: "To do",
      tasks: scrumMemberTasks
        .filter((task) => {
          const column = data?.columns.find(({ id }) => id === task.columnId);
          return !reviewColumnIds.has(task.columnId) && column?.category === "todo";
        })
        .sort(byPriority),
    },
  ];

  if (!data) return error ? <ErrorBanner>{error}</ErrorBanner> : <Skeleton label="Loading tasks…" variant={mode === "board" ? "board" : "list"} />;
  const canManageTasks = isAdmin || data.permissions.includes("manage_tasks");
  const canManageSprints = isAdmin || data.permissions.includes("manage_sprints");
  return (
    <section className="task-workspace">
      <div className="task-toolbar">
        {mode !== "scrum" && <div className="task-filters">
          <label className="task-search"><i aria-hidden="true" className="bi bi-search" /><input aria-label={mode === "board" ? "Search board" : "Search backlog"} onChange={(event) => { setQuery(event.target.value); onQueryChange?.(event.target.value); }} placeholder={`Search ${mode === "backlog" ? "backlog" : "board"}`} value={query} /></label>
          <div className="assignee-filter" aria-label="Filter by assignee" role="group">
            <button
              aria-label="Unassigned"
              aria-pressed={assignee === "unassigned"}
              className="member-avatar assignee-unassigned"
              onClick={() => setAssignee(assignee === "unassigned" ? "" : "unassigned")}
              title="Unassigned"
              type="button"
            >
              <i aria-hidden="true" className="bi bi-person" />
            </button>
            {assigneeFilterMembers.slice(0, ASSIGNEE_AVATAR_LIMIT).map((member) => <button aria-pressed={assignee === member.id} className="member-avatar" key={member.id} onClick={() => setAssignee(assignee === member.id ? "" : member.id)} title={member.displayName} type="button"><AvatarContent allNames={memberNames} avatarUrl={member.avatarUrl} name={member.displayName} /></button>)}
            {assigneeFilterMembers.length > ASSIGNEE_AVATAR_LIMIT && (() => {
              const overflowMembers = assigneeFilterMembers.slice(ASSIGNEE_AVATAR_LIMIT);
              const activeOverflowMember = overflowMembers.find((member) => member.id === assignee);
              return (
                <span className="assignee-overflow">
                  <button
                    aria-expanded={assigneeOverflowOpen}
                    aria-label={activeOverflowMember ? `Filtered by ${activeOverflowMember.displayName}. ${overflowMembers.length} more assignees` : `${overflowMembers.length} more assignees`}
                    aria-pressed={Boolean(activeOverflowMember)}
                    className="member-avatar assignee-overflow-trigger"
                    onClick={() => setAssigneeOverflowOpen((value) => !value)}
                    type="button"
                  >
                    {activeOverflowMember ? <AvatarContent allNames={memberNames} avatarUrl={activeOverflowMember.avatarUrl} name={activeOverflowMember.displayName} /> : `+${overflowMembers.length}`}
                  </button>
                  {assigneeOverflowOpen && (
                    <>
                      <div className="type-picker-backdrop" onClick={() => setAssigneeOverflowOpen(false)} />
                      <div className="type-picker-menu assignee-overflow-menu" role="menu">
                        {overflowMembers.map((member) => (
                          <button
                            aria-current={assignee === member.id}
                            key={member.id}
                            onClick={() => { setAssignee(assignee === member.id ? "" : member.id); setAssigneeOverflowOpen(false); }}
                            role="menuitem"
                            type="button"
                          >
                            <span className="member-avatar"><AvatarContent allNames={memberNames} avatarUrl={member.avatarUrl} name={member.displayName} /></span>
                            {member.displayName}
                          </button>
                        ))}
                      </div>
                    </>
                  )}
                </span>
              );
            })()}
          </div>
          <select aria-label="Filter" onChange={(event) => setType(event.target.value)} value={type}><option value="">Filter</option><option value="epic">Epic</option><option value="story">Story</option><option value="task">Task</option><option value="subtask">Subtask</option><option value="bug">Bug</option></select>
          <button className="button secondary" disabled={!query && !type && !assignee} onClick={() => { setQuery(""); setType(""); setAssignee(""); onQueryChange?.(""); }} type="button">Clear filters</button>
          {mode === "board" && data.sprints.length > 0 && <select aria-label="Sprint view" onChange={(event) => onNavigate({ mode, sprintId: event.target.value || null })} value={sprintId ?? ""}>{!sprintId && <option disabled value="">Select a sprint</option>}{data.sprints.map((sprint) => <option key={sprint.id} value={sprint.id}>{sprint.name}</option>)}</select>}
          {mode === "board" && (
            <button
              aria-pressed={groupBy === "assignee"}
              className="group-by-toggle"
              onClick={() => setGroupBy((value) => (value === "assignee" ? "none" : "assignee"))}
              type="button"
            >
              <i aria-hidden="true" className="bi bi-layers" /> Group: {groupBy === "assignee" ? "Assignee" : "None"}
            </button>
          )}
        </div>}
        {mode === "scrum" && <label className="scrum-sprint-filter">Sprint<select aria-label="Sprint view" onChange={(event) => onNavigate({ mode, sprintId: event.target.value || null })} value={sprintId ?? ""}>
          <option value="">All active sprints</option>
          {data.sprints.filter(({ state }) => state === "active").map((sprint) => <option key={sprint.id} value={sprint.id}>{sprint.name}</option>)}
        </select></label>}
        {mode !== "scrum" && <button className="button primary" onClick={() => setCreateOpen(true)} type="button">+ Create task</button>}
        {mode === "board" && (isAdmin || data.permissions.includes("manage_board")) && <button aria-label="Configure board" className="chrome-icon-button" onClick={() => setBoardSettingsOpen(true)} title="Configure board" type="button"><i aria-hidden="true" className="bi bi-sliders" /></button>}
      </div>
      <ErrorBanner>{error}</ErrorBanner>
      {creationNotice && <div className="banner-notice" role="status"><span>{creationNotice}</span><button className="text-button" onClick={() => { setQuery(""); setType(""); setAssignee(""); onQueryChange?.(""); onNavigate({ mode, query: "", sprintId: null }); setCreationNotice(""); }} type="button">Show task</button></div>}
      {mode === "board" && boardNeedsSprint ? (
        <div className="empty-state compact">
          <h2>No active sprint</h2>
          <p>Start a sprint, or pick a planned one above, to see its work on the board.</p>
          {onOpenSprints && <button className="button secondary" onClick={onOpenSprints} type="button">Go to Sprints</button>}
        </div>
      ) : mode === "board" ? (
        <div className={`task-board${groupBy === "assignee" ? " grouped-board" : ""}`}>
          {boardGroups.map((group) => (
            <section className="board-swimlane" key={group.id}>
              {groupBy === "assignee" && (() => {
                const lanePoints = group.tasks.reduce((sum, task) => sum + (task.estimatePoints ?? 0), 0);
                return <header className="swimlane-header"><span className="member-avatar"><AvatarContent allNames={memberNames} avatarUrl={memberById(members, group.id)?.avatarUrl} name={group.name} /></span><strong>{group.name}</strong><span aria-label={`${group.tasks.length} work item${group.tasks.length === 1 ? "" : "s"}`} className="count-badge">{group.tasks.length}</span><span aria-label={`${lanePoints} point${lanePoints === 1 ? "" : "s"}`} className="count-badge points-badge">{lanePoints} pt</span><button aria-expanded={!collapsedGroups.has(group.id)} aria-label={`${collapsedGroups.has(group.id) ? "Expand" : "Collapse"} ${group.name}`} onClick={() => setCollapsedGroups((value) => { const next = new Set(value); if (next.has(group.id)) next.delete(group.id); else next.add(group.id); return next; })} type="button">{collapsedGroups.has(group.id) ? "⌄" : "⌃"}</button></header>;
              })()}
              {!collapsedGroups.has(group.id) && <div className="swimlane-columns" style={{ gridTemplateColumns: `repeat(${data.columns.length}, minmax(220px, 1fr))` }}>
                {data.columns.map((column) => {
                  const columnTasks = group.tasks.filter((task) => task.columnId === column.id);
                  const columnPoints = columnTasks.reduce((sum, task) => sum + (task.estimatePoints ?? 0), 0);
                  // WIP limits apply per assignee per column (see boardColumnUsage
                  // in tasks-api.ts). In an assignee swimlane the count is just
                  // that person's tasks; ungrouped, show the busiest assignee's
                  // count so the badge still means "someone is at the limit".
                  const wipCount = groupBy === "assignee"
                    ? columnTasks.length
                    : Math.max(0, ...Object.values(columnTasks.reduce<Record<string, number>>((counts, task) => {
                        counts[task.assigneeId] = (counts[task.assigneeId] ?? 0) + 1;
                        return counts;
                      }, {})));
                  return <section
                    aria-label={column.name}
                    className={`task-column${dragTargetColumnId === `${group.id}:${column.id}` ? " drag-target" : ""}`}
                    key={column.id}
                    onDragEnter={(event: DragEvent) => { event.preventDefault(); setDragTargetColumnId(`${group.id}:${column.id}`); }}
                    onDragLeave={(event: DragEvent) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDragTargetColumnId(null); }}
                    onDragOver={(event: DragEvent) => { event.preventDefault(); setDragTargetColumnId(`${group.id}:${column.id}`); }}
                    onDrop={() => {
                      const task = visibleTasks.find(({ id }) => id === draggedTaskId);
                      if (task) void requestMove(task, column);
                      setDraggedTaskId(null); setDragTargetColumnId(null); setReorderTargetTaskId(null);
                    }}
                  >
                    <header><h2>{column.name}</h2><div className="column-badges"><span aria-label={`${columnPoints} point${columnPoints === 1 ? "" : "s"}`} className="count-badge">{columnPoints}</span>{column.wipLimit ? <span aria-label={`${wipCount} of ${column.wipLimit} work items per assignee`} className={`count-badge wip-badge${wipCount > column.wipLimit ? " wip-over" : ""}`}>{wipCount}/{column.wipLimit}</span> : null}</div></header>
                    <div className="task-column-cards">
                      {columnTasks.map((task) => <TaskCard
                        columns={data.columns}
                        isReorderTarget={reorderTargetTaskId === task.id}
                        key={task.id}
                        members={members}
                        onAssigneeChange={changeAssignee}
                        onCardDragEnter={() => { if (task.id !== draggedTaskId) setReorderTargetTaskId(task.id); }}
                        onCardDrop={() => {
                          // Dropping a card directly onto another card inserts it immediately
                          // above the target -- interpolated between the target's and its
                          // previous sibling's position, instead of always appending to the
                          // bottom (see moveTask's default `position`). Dropping in empty
                          // column space (this card's onDrop never fires) keeps that old,
                          // append-to-bottom behavior via the column-level onDrop above.
                          setReorderTargetTaskId(null);
                          const dragged = visibleTasks.find(({ id }) => id === draggedTaskId);
                          if (dragged && dragged.id !== task.id) {
                            const siblings = columnTasks.filter(({ id }) => id !== draggedTaskId);
                            const targetIndex = siblings.findIndex(({ id }) => id === task.id);
                            const previousSibling = siblings[targetIndex - 1];
                            const position = previousSibling ? (previousSibling.position + task.position) / 2 : task.position - 1000;
                            void requestMove(dragged, column, position);
                          }
                          setDraggedTaskId(null); setDragTargetColumnId(null);
                        }}
                        onDragEnd={() => { setDraggedTaskId(null); setDragTargetColumnId(null); setReorderTargetTaskId(null); }}
                        onDragStart={setDraggedTaskId}
                        onEstimateChange={changeEstimatePoints}
                        onMove={requestMove}
                        onOpen={(selected) => onNavigate({ mode, sprintId, taskId: selected.id })}
                        onPriorityChange={changePriority}
                        onRefreshPullRequest={refreshCardPullRequest}
                        onReviewersChange={changeReviewers}
                        task={task}
                      />)}
                      {draggedTaskId && dragTargetColumnId === `${group.id}:${column.id}` && <div className="task-drop-preview">Drop in {column.name}</div>}
                    </div>
                    <button className="column-create" onClick={() => setCreateOpen(true)} type="button">+ Create</button>
                  </section>;
                })}
              </div>}
            </section>
          ))}
          {!boardGroups.length && <div className="empty-state compact"><h2>No matching work items</h2><p>Adjust your filters or create a task.</p></div>}
        </div>
      ) : mode === "backlog" ? (
        <div className="backlog-groups">
          {canManageSprints && (
            <div className="backlog-sprint-create">
              <button className="button secondary" onClick={() => setSprintCreateOpen((value) => !value)} type="button">{sprintCreateOpen ? "Cancel" : "+ Create sprint"}</button>
              {sprintCreateOpen && <SprintCreateForm busy={sprintCreateBusy} onSubmit={(event) => void createSprintFromBacklog(event)} />}
            </div>
          )}
          {[...data.sprints.filter(({ state }) => ["active", "planned"].includes(state)).map((sprint) => ({ id: sprint.id, name: sprint.name, state: sprint.state, tasks: data.tasks.filter((task) => task.sprintId === sprint.id) })), { id: "backlog", name: "Backlog", state: "backlog", tasks: data.tasks.filter((task) => !task.sprintId) }].map((group) => (
            <section className={`task-backlog${canManageTasks ? " selectable" : ""}`} key={group.id}>
              <header>
                <div><button aria-expanded={!collapsedBacklogGroups.has(group.id)} aria-label={`${collapsedBacklogGroups.has(group.id) ? "Expand" : "Collapse"} ${group.name}`} onClick={() => setCollapsedBacklogGroups((value) => { const next = new Set(value); if (next.has(group.id)) next.delete(group.id); else next.add(group.id); return next; })} type="button">{collapsedBacklogGroups.has(group.id) ? "⌃" : "⌄"}</button><strong>{group.name}</strong><span>{group.tasks.length} work item{group.tasks.length === 1 ? "" : "s"}</span></div>
                <div className="backlog-summary">
                  {data.columns.map((column) => {
                    const columnPoints = group.tasks.filter((task) => task.columnId === column.id).reduce((sum, task) => sum + (task.estimatePoints ?? 0), 0);
                    return <span aria-label={`${columnPoints} point${columnPoints === 1 ? "" : "s"} in ${column.name}`} key={column.id}>{columnPoints} {column.name}</span>;
                  })}
                  {group.state === "active" && (
                    <button
                      className="button secondary"
                      onClick={async () => {
                        const ok = await confirm({
                          title: `Complete ${group.name}? Unfinished work will move to the backlog.`,
                        });
                        if (!ok) return;
                        setError("");
                        try {
                          await api(`/api/sprints/${group.id}/complete`, {
                            body: JSON.stringify({ destinationSprintId: null }),
                            method: "POST",
                          });
                          await load();
                        } catch (reason) {
                          setError(reason instanceof Error ? reason.message : "Could not complete sprint.");
                        }
                      }}
                      type="button"
                    >
                      Complete sprint
                    </button>
                  )}
                  {canManageTasks && group.tasks.length > 0 && (
                    <div className="bulk-actions">
                      <button
                        className="button secondary"
                        onClick={() => setSelectedBacklogTasks((value) => {
                          const groupIds = group.tasks.map(({ id }) => id);
                          const allSelected = groupIds.every((id) => value.has(id));
                          const next = new Set(value);
                          if (allSelected) groupIds.forEach((id) => next.delete(id));
                          else groupIds.forEach((id) => next.add(id));
                          return next;
                        })}
                        type="button"
                      >
                        {group.tasks.every(({ id }) => selectedBacklogTasks.has(id)) ? "Clear selection" : "Select all visible"}
                      </button>
                      {canManageSprints && (
                        <select
                          aria-label="Move selected work items to sprint"
                          disabled={bulkMoveBusy || !group.tasks.some(({ id }) => selectedBacklogTasks.has(id))}
                          onChange={(event) => {
                            const value = event.target.value;
                            event.target.value = "";
                            if (value) void bulkMoveSelectedToSprint(value === "__backlog__" ? "" : value);
                          }}
                          value=""
                        >
                          <option disabled value="">Move to sprint…</option>
                          <option value="__backlog__">Backlog</option>
                          {data.sprints.filter(({ state }) => ["planned", "active"].includes(state)).map((sprint) => <option key={sprint.id} value={sprint.id}>{sprint.name}</option>)}
                        </select>
                      )}
                      <button
                        className="button danger"
                        disabled={bulkDeleteBusy || !group.tasks.some(({ id }) => selectedBacklogTasks.has(id))}
                        onClick={() => void bulkDeleteSelected()}
                        type="button"
                      >
                        Delete selected{group.tasks.filter(({ id }) => selectedBacklogTasks.has(id)).length > 0 ? ` (${group.tasks.filter(({ id }) => selectedBacklogTasks.has(id)).length})` : ""}
                      </button>
                    </div>
                  )}
                </div>
              </header>
              {!collapsedBacklogGroups.has(group.id) && <>
              {group.tasks.map((task) => (
                <article aria-label={`${task.taskKey}: ${task.title}`} key={task.id}>
                  {canManageTasks && (
                    <input
                      aria-label={`Select ${task.taskKey}`}
                      checked={selectedBacklogTasks.has(task.id)}
                      onChange={(event) => setSelectedBacklogTasks((value) => {
                        const next = new Set(value);
                        if (event.target.checked) next.add(task.id);
                        else next.delete(task.id);
                        return next;
                      })}
                      type="checkbox"
                    />
                  )}
                  <button className="backlog-open" onClick={() => onNavigate({ mode, sprintId, taskId: task.id })} type="button"><IssueTypeIcon type={task.type} /><strong>{task.taskKey}</strong><span>{task.title}</span></button>
                  <span className={epicParentLabel(task) ? "epic-chip" : "epic-chip epic-chip-hidden"}>{epicParentLabel(task) ?? ""}</span>
                  <span className="parent-chip">{task.type}</span>
                  <select aria-label={`Status for ${task.taskKey}`} onChange={(event) => { const column = data.columns.find(({ id }) => id === event.target.value); if (column) void requestMove(task, column); }} value={task.columnId}>{data.columns.map((column) => <option key={column.id} value={column.id}>{column.name}</option>)}</select>
                  <PointsEditor
                    ariaLabel={`Change story points for ${task.taskKey} (currently ${task.estimatePoints ?? "unestimated"})`}
                    onChange={(estimatePoints) => void changeEstimatePoints(task, estimatePoints)}
                    value={task.estimatePoints}
                  />
                  <FieldPicker
                    ariaLabel={`Change priority for ${task.taskKey} (currently ${priorityMeta(task.priority).label})`}
                    items={PRIORITY_OPTIONS.map((option) => ({ id: option.value, label: option.label }))}
                    name="priority"
                    onSelect={(priority) => void changePriority(task, priority)}
                    renderLabel={(option) => <><PriorityIcon priority={option.id} />{option.label}</>}
                    renderTrigger={<PriorityIcon priority={task.priority} />}
                    selectedId={task.priority}
                  />
                  <FieldPicker
                    ariaLabel={`Change assignee for ${task.taskKey} (currently ${task.assigneeName ?? "Unassigned"})`}
                    items={members}
                    name="assigneeId"
                    onSelect={(assigneeId) => void changeAssignee(task, assigneeId)}
                    renderLabel={(member) => <><span className="member-avatar"><AvatarContent allNames={memberNames} avatarUrl={member.avatarUrl} name={member.displayName} /></span>{member.displayName}</>}
                    renderTrigger={<span className="member-avatar" title={task.assigneeName}><AvatarContent allNames={memberNames} avatarUrl={memberById(members, task.assigneeId)?.avatarUrl} name={task.assigneeName} /></span>}
                    selectedId={task.assigneeId}
                  />
                  <select aria-label={`Sprint for ${task.taskKey}`} onChange={(event) => void assignSprint(task.id, event.target.value)} value={task.sprintId ?? ""}><option value="">Backlog</option>{data.sprints.filter(({ state }) => ["planned", "active"].includes(state)).map((sprint) => <option key={sprint.id} value={sprint.id}>{sprint.name}</option>)}</select>
                </article>
              ))}
              <button className="backlog-create" onClick={() => setCreateOpen(true)} type="button">+ Create work item</button>
              </>}
            </section>
          ))}
        </div>
      ) : (
        <section className="daily-scrum" aria-label="Daily scrum">
          <header>
            <div>
              <p className="eyebrow">Daily scrum · {scrumEligibleMembers.length ? scrumMemberIndex + 1 : 0} of {scrumEligibleMembers.length}</p>
              <h2>{scrumMember?.displayName ?? "No one has active sprint tasks"}</h2>
              <p>In review and in progress work, plus to-do work ordered by priority — flagged when there has been no comment in the last business day.</p>
            </div>
            {Boolean(scrumEligibleMembers.length) && <div className="scrum-navigation">
              <button className="button secondary" disabled={scrumMemberIndex === 0} onClick={() => setScrumMemberIndex((index) => Math.max(0, index - 1))} type="button">← Previous</button>
              <button className="button primary" disabled={scrumMemberIndex >= scrumEligibleMembers.length - 1} onClick={() => setScrumMemberIndex((index) => Math.min(scrumEligibleMembers.length - 1, index + 1))} type="button">Next person →</button>
            </div>}
          </header>
          {Boolean(scrumEligibleMembers.length) && <div className="scrum-progress" aria-label="Scrum participant progress"><span style={{ width: `${((scrumMemberIndex + 1) / scrumEligibleMembers.length) * 100}%` }} /></div>}
          {scrumGroups.map((group) => (
            <div className="scrum-group" key={group.key}>
              <h3>{group.label} <span className="scrum-group-count">{group.tasks.length}</span></h3>
              <div className="scrum-task-list">
                {group.tasks.map((task) => {
                  const needsUpdate = group.flagStale && isStaleForScrum(task.lastCommentAt);
                  const priority = priorityMeta(task.priority);
                  return (
                    <article className={`scrum-task${needsUpdate ? " needs-update" : ""}`} key={task.id}>
                      <div>
                        <strong>{task.taskKey}</strong>
                        <span>{task.status.replace("_", " ")}</span>
                        <span className="scrum-task-priority" style={{ color: priority.color }}>
                          <i aria-hidden="true" className={`bi ${priority.icon}`} />
                          {priority.label}
                        </span>
                      </div>
                      <h3>{task.title}</h3>
                      <p>{needsUpdate ? "No comment in the last business day." : task.commentCount ? `${task.commentCount} comment${Number(task.commentCount) === 1 ? "" : "s"}` : "Not started"}</p>
                      <button className="button secondary" onClick={() => onNavigate({ mode, sprintId, taskId: task.id })} type="button">Open task</button>
                    </article>
                  );
                })}
                {!group.tasks.length && <p className="scrum-group-empty">Nothing {group.label.toLowerCase()}.</p>}
              </div>
            </div>
          ))}
          {!scrumEligibleMembers.length && <div className="empty-state compact"><h2>No active sprint work to review</h2><p>Assign work in an active sprint to see teammates in the daily scrum.</p></div>}
        </section>
      )}
      {createOpen && (
        <NewTaskDialog
          columns={data.columns}
          currentUserId={currentUserId}
          defaultAssigneeId={assignee || undefined}
          defaultSprintId={sprintId}
          members={members}
          onClose={() => setCreateOpen(false)}
          onCreated={async (created) => {
            const hiding = [
              query && !created.title.toLocaleLowerCase().includes(query.toLocaleLowerCase()) ? "search" : "",
              type && type !== created.type ? "type" : "",
              assignee && assignee !== created.assigneeId ? "assignee" : "",
              sprintId && sprintId !== created.sprintId ? "sprint" : "",
            ].filter(Boolean);
            await load();
            setCreateOpen(false);
            if (hiding.length) setCreationNotice(`Task created. It is hidden by the ${hiding.join(", ")} filter${hiding.length === 1 ? "" : "s"}.`);
          }}
          sprints={data.sprints}
          teamId={teamId}
        />
      )}
      {reviewEntry?.step === "link" && (
        <ReviewEntryDialog
          busy={reviewEntry.busy}
          columnName={reviewEntry.column.name}
          displayName={memberById(members, currentUserId)?.displayName ?? "you"}
          error={reviewEntry.error}
          onCancel={() => setReviewEntry(null)}
          onSubmit={(link) => void submitReviewEntry(link)}
        />
      )}
      {reviewEntry?.step === "reviewers" && (
        <ReviewerSelectDialog
          busy={reviewEntry.busy}
          error={reviewEntry.error}
          members={members}
          onDone={() => void finishReviewEntry()}
          onToggle={(reviewerIds) => void setReviewEntryReviewers(reviewerIds)}
          selectedIds={new Set(
            (data.tasks.find(({ id }) => id === reviewEntry.task.id)?.reviewers ?? reviewEntry.task.reviewers)
              .map((reviewer) => reviewer.reviewerId),
          )}
        />
      )}
      {taskId && data.tasks.find((task) => task.id === taskId) && (
        <TaskDetailsDialog
          canDelete={isAdmin || data.permissions.includes("manage_tasks")}
          columns={data.columns}
          currentUserId={currentUserId}
          goals={data.goals}
          members={members}
          onClose={() => onNavigate({ mode, sprintId, taskId: null })}
          onDeleted={load}
          onUpdated={patchTask}
          sprints={data.sprints}
          task={data.tasks.find((task) => task.id === taskId)!}
          taskUrl={`${window.location.origin}/t/${encodeURIComponent(data.tasks.find((task) => task.id === taskId)!.taskKey)}`}
          tasks={data.tasks}
        />
      )}
      {boardSettingsOpen && <BoardSettings columns={data.columns} onChanged={load} onClose={() => setBoardSettingsOpen(false)} teamId={teamId} />}
      {!isAdmin && data.permissions.includes("manage_tasks") && <span className="sr-only">You have delegated task management permission.</span>}
    </section>
  );
}
